import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Search,
  Sparkles,
  Loader2,
  X as XIcon,
  MessageSquarePlus,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  BookmarkPlus,
  Download,
  FileText,
  Trash2,
  Home,
  MoreHorizontal,
  Building2,
  Share2,
} from 'lucide-react';
import { ProductCard, ProductDisplay, type ProductDisplayData, type ProductItem } from './index';
import {
  productSemanticSearch,
  streamCollaboratorInquire,
  collaboratorChatFilter,
  fetchCategorySuggestions,
  collaboratorProspectBootstrap,
  type CollaboratorBoosts,
  type CollaboratorChatContext,
  type CollaboratorFilterResponse,
  type CollaboratorSuggestedAction,
  type CollaboratorFilterTag,
  type CollaboratorConstraintFilter,
  type CollaboratorCategorySuggestion,
  type CollaboratorProspectBootstrapResponse,
  type CollaboratorFocusNode,
  type CollaboratorNodeSummary,
  type CollaboratorContextProduct,
} from '../../services/productCollaboratorApi';
import type {
  CollaboratorLaneStatus,
  CollaboratorProspect,
} from '../../types/collaboratorProspect';
import AgentIcon from '../AgentIcon';
import FeedbackModal from '../FeedbackModal';
import { useFeedback } from '../../hooks/useFeedback';
import { useAuth } from '../../hooks/useAuth';
import type { Agent } from '../../types';
import InquiryFocusGraphD3 from './InquiryFocusGraphD3';
import './ProductCollaboratorView.css';

interface CollaboratorChatMessage {
  id: string;
  role: 'user' | 'agent';
  content: string;
  isStreaming?: boolean;
  // Stable wall-clock timestamp so the action row can show "5:14 PM" exactly
  // like the main chat. Created once when the message is added and never
  // mutated during streaming.
  timestamp: Date;
  // Once `isStreaming` becomes false we flip this to true. The thumbs/comment
  // action row only renders for completed agent messages — mirrors Aldo.
  isCompleted: boolean;
  // For agent messages, the id of the user message that prompted them. Lets
  // the feedback payload carry `userMessage` like the main chat does.
  replyToId?: string;
  suggestedActions?: CollaboratorSuggestedAction[];
  // Graph node this turn was scoped to (focused node at send time). Lets the
  // agent context replay history for the focused node + its ancestors only.
  nodeId?: string;
  // Marks the long-form Inquire analysis turn so follow-up context can always
  // include the most recent one even if it fell outside the history window.
  isInquire?: boolean;
}

interface ProductCollaboratorViewProps {
  imageScale?: number;
  fastMode?: boolean;
  // The active agent (passed down from ChatInterface). Used for the avatar
  // icon, the feedback agentName field, and the thumbs animation logic.
  agent?: Agent | null;
}

/** Called from ChatInterface when the user hits Clear chat in the navbar. */
export interface ProductCollaboratorViewHandle {
  resetSession: () => void;
}

const EMPTY_DISPLAY: ProductDisplayData = {
  type: 'product_display',
  heading: 'Search the catalog',
  products: [],
  facets: [],
};

const COLLAB_WORK_RATIO_KEY = 'ps-collab-work-ratio';
// Legacy persisted-graph keys — the inquiry graph is now ephemeral (cleared on
// each open), so we only keep these to wipe any stale data left by old builds.
const COLLAB_BOOKMARKS_KEY = 'ps-collab-bookmarks-v1';
const COLLAB_INQUIRY_GRAPH_KEY = 'ps-collab-inquiry-graph-v1';

// ─────────────────────────────────────────────────────────────────────
// Inquiry Focus Graph — spatial node-link tree of the user's inquiry.
//   root (Inquiry) → topic (one per search) → sku leaves (highlights)
// Nodes can be `note` placeholders the user adds manually. Pinned nodes
// survive page refresh; un-pinned ones are session-only.
// ─────────────────────────────────────────────────────────────────────
type InquiryNodeKind =
  | 'root'
  | 'customer'
  | 'topic'
  | 'sku'
  | 'note'
  | 'lane'
  | 'class'
  | 'filter';

/** A bookmarked product carried as metadata on a `class` node. */
interface NodeProduct {
  sku: string;
  name?: string;
  image?: string;
  classDescription?: string;
  subClass?: string;
  stdCost?: number;
  /** Orderable item count for the product (color/size variants). */
  variantCount?: number;
}

interface InquiryNode {
  id: string;
  kind: InquiryNodeKind;
  label: string;
  parentId: string | null;
  childIds: string[];
  pinned?: boolean;
  // customer root (prospect)
  prospect?: CollaboratorProspect;
  // garment lane (prospect workflow)
  laneStatus?: CollaboratorLaneStatus;
  searchQuery?: string;
  rationale?: string;
  // class node (bookmarked products grouped by class_description)
  classDescription?: string;
  products?: NodeProduct[];
  // Progressive qualitative summary — maintained by the agent + user.
  // Children inherit their ancestors' wants/constraints as context.
  wants?: string[];
  constraints?: string[];
  // topic-specific
  query?: string;
  boosts?: CollaboratorBoosts;
  filters?: string;
  filterTags?: CollaboratorFilterTag[];
  highlightedStyleCodes?: string[];
  productCount?: number;
  highlightSnapshot?: Array<{ sku: string; name?: string; image?: string }>;
  // sku-specific (one bookmarked product = one collapsed SKU child node)
  sku?: string;
  stdCost?: number;
  variantCount?: number;
  image?: string;
  // Full product record for the bookmarked SKU. Lets a parent focus re-display
  // bookmarked items even when they came from a DIFFERENT search than the one
  // currently loaded (the graph is ephemeral, so carrying the item is cheap).
  item?: ProductItem;
  // filter node — a constraint resolved to a real catalog filter. Applies to
  // its parent node and ALL descendants. Draggable: reparent to the root to
  // make it inquiry-wide. Excluded from cost/count rollups.
  filterOData?: string;
  filterTerm?: string;
  filterConfidence?: number;
  filterTag?: CollaboratorFilterTag | null;
  createdAt: number;
}

/** Own + inherited (subtree) cost/count metrics for one node. */
interface NodeRollup {
  ownProducts: number;
  ownItems: number;
  ownCost: number;
  numProducts: number;
  numItems: number;
  estCost: number;
}

const INDUSTRY_FILTER_NODE_ID = 'node-industry-filter';
const CUSTOMER_ROOT_ID = 'customer-root';

function buildGraphFromProspectBootstrap(
  prospect: CollaboratorProspect,
  bootstrap: CollaboratorProspectBootstrapResponse
): InquiryGraphState {
  const industryId = INDUSTRY_FILTER_NODE_ID;
  const laneIds = (bootstrap.lanes || []).map((l) => l.id);
  const nodes: Record<string, InquiryNode> = {
    [CUSTOMER_ROOT_ID]: {
      id: CUSTOMER_ROOT_ID,
      kind: 'customer',
      label: prospect.company,
      parentId: null,
      childIds: [industryId, ...laneIds],
      prospect,
      createdAt: Date.now(),
    },
    [industryId]: {
      id: industryId,
      kind: 'topic',
      label: `Industry: ${bootstrap.approvedIndustry || prospect.industry}`,
      parentId: CUSTOMER_ROOT_ID,
      childIds: [],
      query: bootstrap.starterQuery || `${prospect.industry} uniforms`,
      filters: bootstrap.filters,
      filterTags: bootstrap.tags,
      pinned: true,
      createdAt: Date.now(),
    },
  };
  for (const lane of bootstrap.lanes || []) {
    nodes[lane.id] = {
      id: lane.id,
      kind: 'lane',
      label: lane.label,
      parentId: CUSTOMER_ROOT_ID,
      childIds: [],
      searchQuery: lane.searchQuery,
      laneStatus: lane.status || 'pending',
      rationale: lane.rationale,
      createdAt: Date.now(),
    };
  }
  return { rootId: CUSTOMER_ROOT_ID, nodes };
}

function formatMoney(n: number): string {
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Resolve the class_description bucket a product belongs to. */
function classDescriptionOf(p: ProductItem): string {
  const a = (p.attributes || {}) as Record<string, unknown>;
  const raw =
    (a.class_description as string) ||
    (a.sub_class_description as string) ||
    (a.product_sub_type_category as string) ||
    (a.category as string) ||
    '';
  return (raw || 'Uncategorized').trim();
}

function productStdCost(p: ProductItem): number {
  const a = (p.attributes || {}) as Record<string, unknown>;
  const fromAttr = typeof a.std_cost === 'number' ? (a.std_cost as number) : NaN;
  if (!Number.isNaN(fromAttr) && fromAttr > 0) return fromAttr;
  const priceNum = parseFloat(String(p.price ?? '').replace(/[^0-9.]/g, ''));
  return Number.isNaN(priceNum) ? 0 : priceNum;
}

/** Snapshot a ProductItem into the compact metadata stored on a class node. */
function toNodeProduct(p: ProductItem): NodeProduct {
  const a = (p.attributes || {}) as Record<string, unknown>;
  return {
    sku: p.base_sku || (a.style_code as string) || p.partialSku_OPIM || '',
    name: p.name || '',
    image: p.primary_image || p.color_variants?.[0]?.image_url || '',
    classDescription: classDescriptionOf(p),
    subClass: (a.sub_class_description as string) || '',
    stdCost: productStdCost(p),
    variantCount: Math.max(1, (p.color_variants || []).length || 1),
  };
}

function formatCustomerGraphLine(prospect: CollaboratorProspect): string {
  const parts = [
    `Account ID:${prospect.accountId}`,
    `Company:${prospect.company}`,
  ];
  if (prospect.contact) parts.push(`Contact:${prospect.contact}`);
  parts.push(`Industry:${prospect.industry}`);
  if (prospect.region) parts.push(`Region:${prospect.region}`);
  if (prospect.salesRepType) parts.push(`Sales Rep Type:${prospect.salesRepType}`);
  return parts.join(' • ');
}

interface InquiryGraphState {
  rootId: string;
  nodes: Record<string, InquiryNode>;
}

const INQUIRY_ROOT_ID = 'inquiry-root';

function emptyInquiryGraph(): InquiryGraphState {
  return {
    rootId: INQUIRY_ROOT_ID,
    nodes: {
      [INQUIRY_ROOT_ID]: {
        id: INQUIRY_ROOT_ID,
        kind: 'root',
        label: 'Inquiry',
        parentId: null,
        childIds: [],
        createdAt: Date.now(),
      },
    },
  };
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 40);
}

/** Remove any `<field> eq '...'` clause(s) (OR-combined, optionally
 * parenthesized) from an OData filter and tidy dangling operators. */
function stripFieldEqClause(odata: string, field: string): string {
  const re = new RegExp(
    `\\(?\\s*${field} eq '[^']+'(?:\\s+or\\s+${field} eq '[^']+')*\\s*\\)?`,
    'gi',
  );
  return (odata || '')
    .replace(re, '')
    .replace(/\(\s*\)/g, '')
    .replace(/^\s*(and|or)\s+/i, '')
    .replace(/\s+(and|or)\s*$/i, '')
    .replace(/\s+(and|or)\s+(and|or)\s+/gi, ' $2 ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Matches the industry filter clause — one or more `search.ismatch(...'industry'...)`
// calls, optionally OR-combined and parenthesized — so it can be removed/replaced.
const INDUSTRY_CLAUSE_RE =
  /\(?\s*search\.ismatch\([^)]*'industry'[^)]*\)(?:\s+or\s+search\.ismatch\([^)]*'industry'[^)]*\))*\s*\)?/gi;

/** Remove any industry ismatch clause(s) from an OData filter and tidy operators. */
function stripIndustryClause(odata: string): string {
  return (odata || '')
    .replace(INDUSTRY_CLAUSE_RE, '')
    .replace(/\(\s*\)/g, '')
    .replace(/^\s*(and|or)\s+/i, '')
    .replace(/\s+(and|or)\s*$/i, '')
    .replace(/\s+(and|or)\s+(and|or)\s+/gi, ' $2 ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

const MIN_WORK_RATIO = 0.4;
const MAX_WORK_RATIO = 0.8;
const PRODUCT_ANCHOR_RE = /\[(?:PRODUCTS|PRODUCT_CARDS):[^\]]+\]/g;
const HIGHLIGHTED_OPTIONS_HEADING_RE = /^(#{1,6}\s*)?Highlighted Options\s*$/gim;

interface CollaboratorBookmark {
  id: string;
  label: string;
  query: string;
  boosts: CollaboratorBoosts;
  filters: string;
  filterTags: CollaboratorFilterTag[];
  highlightedStyleCodes: string[];
  productCount: number;
  highlightSnapshot: Array<{ sku: string; name?: string; image?: string }>;
  createdAt: number;
}

function downloadBlob(filename: string, mime: string, content: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}

function csvEscape(value: unknown): string {
  const s = value == null ? '' : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function bookmarkToCsv(b: CollaboratorBookmark): string {
  const rows: string[][] = [
    ['Bookmark', b.label],
    ['Query', b.query],
    ['Active filters (OData)', b.filters],
    ['Products in result', String(b.productCount)],
    ['Highlighted', String(b.highlightedStyleCodes.length)],
    ['Created', new Date(b.createdAt).toISOString()],
    [],
    ['Style Code', 'Name', 'Image'],
    ...b.highlightSnapshot.map((h) => [h.sku, h.name || '', h.image || '']),
  ];
  return rows.map((r) => r.map(csvEscape).join(',')).join('\n');
}

function exportBookmarkPdf(b: CollaboratorBookmark) {
  // Dependency-free PDF export: open a styled HTML window and trigger
  // the browser's print dialog. The user picks "Save as PDF" from there.
  const w = window.open('', '_blank', 'noopener,noreferrer,width=900,height=1100');
  if (!w) return;
  const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] as string));
  const filterChips = (b.filterTags || [])
    .map((t) => `<span class="chip">${esc(t.label)}</span>`)
    .join(' ');
  const rows = (b.highlightSnapshot || [])
    .map(
      (h) =>
        `<tr><td class="sku">${esc(h.sku)}</td><td>${esc(h.name || '')}</td></tr>`
    )
    .join('');
  w.document.write(`<!doctype html><html><head><meta charset="utf-8" />
<title>${esc(b.label)} — Product Searchlight Bookmark</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, system-ui, sans-serif; color: #111; margin: 32px; }
  h1 { font-size: 22px; margin: 0 0 6px; }
  .meta { color: #555; font-size: 12px; margin-bottom: 18px; }
  .chip { display: inline-block; background: #dbeafe; color: #1e3a8a; padding: 2px 8px; border-radius: 999px; font-size: 11px; margin: 2px; }
  table { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 12px; }
  th, td { border-bottom: 1px solid #e3e5e8; padding: 6px 8px; text-align: left; }
  th { background: #f8f9fa; font-weight: 600; }
  .sku { font-family: ui-monospace, Menlo, monospace; color: #1d4ed8; font-weight: 600; }
  .footer { margin-top: 24px; color: #888; font-size: 11px; }
  @media print { body { margin: 18mm; } }
</style></head><body>
  <h1>${esc(b.label)}</h1>
  <div class="meta">
    Query: <strong>${esc(b.query || '(none)')}</strong><br/>
    ${b.productCount} products in result · ${b.highlightedStyleCodes.length} highlighted ·
    saved ${new Date(b.createdAt).toLocaleString()}
  </div>
  <div>${filterChips || '<span class="meta">No filters</span>'}</div>
  <table>
    <thead><tr><th style="width:120px">Style</th><th>Name</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="2" class="meta">No highlighted products in this bookmark.</td></tr>'}</tbody>
  </table>
  <div class="footer">Product Searchlight · Collaborator export</div>
  <script>setTimeout(() => window.print(), 200);</script>
</body></html>`);
  w.document.close();
}

const normStyleToken = (s?: string | null) =>
  s ? String(s).toUpperCase().replace(/[^A-Z0-9]/g, '') : '';

/** The canonical style-code token for a product, used to match/dedup against
 *  highlighted SKUs and bookmarked SKU nodes. */
const productStyleToken = (p: ProductItem): string =>
  normStyleToken(p.base_sku) ||
  normStyleToken(p.partialSku_OPIM) ||
  normStyleToken(p.attributes?.style_code as string | undefined) ||
  '';

function stripBoldAroundStyleCodes(text: string, tokens: string[]): string {
  if (!tokens.length) return text;
  let out = text;
  const sorted = [...tokens].sort((a, b) => b.length - a.length);
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  for (const t of sorted) {
    out = out.replace(new RegExp(`\\*\\*\\s*(${escape(t)})\\s*\\*\\*`, 'gi'), '$1');
  }
  return out;
}

/**
 * Strip single-backtick wrappers around known style codes so the markdown
 * renderer emits them as inline text (linkifiable) instead of `<code>`
 * spans which our component renderer can't intercept reliably. Triple-
 * backtick / multi-line code fences are left alone.
 */
function stripBackticksAroundStyleCodes(text: string, tokens: string[]): string {
  if (!tokens.length) return text;
  let out = text;
  const sorted = [...tokens].sort((a, b) => b.length - a.length);
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  for (const t of sorted) {
    out = out.replace(new RegExp(`(?<!\`)\`\\s*(${escape(t)})\\s*\`(?!\`)`, 'gi'), '$1');
  }
  return out;
}

/**
 * Collect the OData + labels of every `filter` node that applies to `nodeId`.
 * A filter node attached to node X applies to X and ALL descendants — so for a
 * focused node we gather filter children hanging off any node in its ancestor
 * chain (including itself). Reparenting a filter node to the root therefore
 * makes it inquiry-wide.
 */
function computeScopedFilters(
  nodes: Record<string, InquiryNode>,
  nodeId: string
): { odata: string; labels: string[] } {
  const clauses: string[] = [];
  const labels: string[] = [];
  const seen = new Set<string>();
  let cur: InquiryNode | undefined = nodes[nodeId];
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    for (const cid of cur.childIds) {
      const child = nodes[cid];
      if (child && child.kind === 'filter' && child.filterOData) {
        clauses.push(child.filterOData);
        labels.push(child.filterTerm || child.label);
      }
    }
    cur = cur.parentId ? nodes[cur.parentId] : undefined;
  }
  const odata = clauses.map((c) => `(${c})`).join(' and ');
  return { odata, labels };
}

/** AND-combine two OData filter strings, skipping empties. */
function andOData(a: string, b: string): string {
  const left = (a || '').trim();
  const right = (b || '').trim();
  if (!left) return right;
  if (!right) return left;
  return `(${left}) and (${right})`;
}

const ProductCollaboratorView = forwardRef<
  ProductCollaboratorViewHandle,
  ProductCollaboratorViewProps
>(function ProductCollaboratorView({ imageScale = 25, fastMode = true, agent = null }, ref) {
  const [searchInput, setSearchInput] = useState('');
  const [lastQuery, setLastQuery] = useState('');
  const [displayData, setDisplayData] = useState<ProductDisplayData>(EMPTY_DISPLAY);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [visibleProducts, setVisibleProducts] = useState<ProductItem[]>([]);
  const [highlightedStyleCodes, setHighlightedStyleCodes] = useState<string[]>([]);
  // Quick category-filter chips suggested by the last search (semantic match
  // vs the category hierarchy). Clickable to apply an OR filter; toggle off.
  const [categorySuggestions, setCategorySuggestions] = useState<
    CollaboratorCategorySuggestion[]
  >([]);
  // True while the (async) narrow-filter match is in flight.
  const [categorySuggestLoading, setCategorySuggestLoading] = useState(false);
  const [activeBoosts, setActiveBoosts] = useState<CollaboratorBoosts>({});
  const [activeFilterTags, setActiveFilterTags] = useState<CollaboratorFilterTag[]>([]);
  // OData filter string built by the FilterAgent on the backend.
  // Empty string ↔ no hard filters. We thread this into every search.
  const [activeFilters, setActiveFilters] = useState<string>('');
  const [chatInput, setChatInput] = useState('');
  // Free-text question submitted directly to Inquire (e.g. "which is best for
  // outdoors?") — runs an inquiry on the same product set with the question.
  const [inquireFollowUp, setInquireFollowUp] = useState('');
  const [isFiltering, setIsFiltering] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isInquiring, setIsInquiring] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<CollaboratorChatMessage[]>([]);
  const [workRatio, setWorkRatio] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(COLLAB_WORK_RATIO_KEY);
      const parsed = stored ? parseFloat(stored) : NaN;
      if (!Number.isNaN(parsed) && parsed >= MIN_WORK_RATIO && parsed <= MAX_WORK_RATIO) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return 0.62;
  });
  const [isDragging, setIsDragging] = useState(false);
  // Inquiry graph is ephemeral — it starts empty on every open. Direct JSON
  // kickoff (API) or NL kickoff (chat) seeds it; nothing is persisted.
  const [inquiryGraph, setInquiryGraph] = useState<InquiryGraphState>(() =>
    emptyInquiryGraph()
  );
  const [focusedNodeId, setFocusedNodeId] = useState<string>(INQUIRY_ROOT_ID);
  // Expanded topics — SKU leaves are hidden under their parent topic until
  // the user expands them. Defaults to empty (= everything collapsed).
  const [expandedTopicIds, setExpandedTopicIds] = useState<Set<string>>(
    () => new Set()
  );
  // Signature of the last search that produced a topic node, used to
  // gate the upsert effect so it only fires on real changes.
  const lastSearchSignatureRef = useRef<string>('');
  const industryTop10Ref = useRef<{ codes: string[]; name: string; names: string[] }>({
    codes: [],
    name: '',
    names: [],
  });
  const activeLaneIdRef = useRef<string | null>(null);
  // Prospect kickoff happens via the chat input (NL inference) or the direct
  // JSON API endpoint (/product/collaborator/prospect) — no in-panel form.
  // Focus graph in the chat column — collapsed by default.
  const [focusGraphExpanded, setFocusGraphExpanded] = useState(true);
  // Top-control-bar expandables (kept behind the scenes by default).
  const [showCustomerInfo, setShowCustomerInfo] = useState(false);
  const [showNodeReqs, setShowNodeReqs] = useState(false);
  const inquireAbortRef = useRef<AbortController | null>(null);
  // Remembers the exact product set + query the LAST Inquire ran on, scoped to
  // the node it was focused on. Follow-up chat turns reuse this so they keep the
  // SAME inquiry context (the curated products) even when the focused node has
  // no bookmarked products of its own (e.g. a free-text search that never
  // spawned a topic node). See chatContext.
  const lastInquireRef = useRef<
    { nodeId: string; query: string; products: ProductItem[] } | null
  >(null);
  // Cancels the in-flight category-suggestion match when a new search starts.
  const categorySuggestAbortRef = useRef<AbortController | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Tracks the topic node id corresponding to the current `lastQuery`, so
  // highlight-change effects know which topic to sync SKU leaves under.
  const currentTopicIdRef = useRef<string | null>(null);
  // Mirror graph + focus into refs so `runSearch` (a stable callback) can apply
  // scoped filter-node OData for the current focus without re-creating itself.
  const inquiryGraphRef = useRef<InquiryGraphState>(inquiryGraph);
  const focusedNodeIdRef = useRef<string>(focusedNodeId);
  useEffect(() => {
    inquiryGraphRef.current = inquiryGraph;
  }, [inquiryGraph]);
  useEffect(() => {
    focusedNodeIdRef.current = focusedNodeId;
  }, [focusedNodeId]);

  // Wipe any graph persisted by older builds so reopening is always a clean slate.
  useEffect(() => {
    try {
      localStorage.removeItem(COLLAB_INQUIRY_GRAPH_KEY);
      localStorage.removeItem(COLLAB_BOOKMARKS_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Feedback wiring — uses the SAME hooks/components as Aldo's main chat
  // (useFeedback → feedbackService → POST /api/feedback → Azure Table).
  // Collaborator owns its own modal state so feedback carries Collaborator's
  // chat history rather than the main chat-mode messages array.
  // ─────────────────────────────────────────────────────────────────────
  const { submitFeedback, getFeedbackForMessage } = useFeedback();
  const { getUserEmail } = useAuth();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [pendingFeedback, setPendingFeedback] = useState<{
    messageId: string;
    feedback: 'positive' | 'negative';
    agentName: string;
    userMessage: string;
    agentMessage: string;
    existingTags?: string[];
    existingNotes?: string;
    notesOnly?: boolean;
    allowFeedbackSelection?: boolean;
    allowUpdate?: boolean;
  } | null>(null);

  // ProductDisplay expects a 0.45-1.0 multiplier; imageScale prop is 0-100 (matching useImageScale).
  const productScale = useMemo(() => {
    if (typeof imageScale !== 'number') return 0.75;
    if (imageScale <= 0) return 0.45;
    if (imageScale >= 100) return 1.0;
    return 0.45 + (imageScale / 100) * 0.55;
  }, [imageScale]);

  const scrollChatToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  // Fire-and-forget category-suggestion match. Cancels any prior in-flight
  // request so a newer search always wins; results land asynchronously.
  const loadCategorySuggestions = useCallback((query: string, filters: string) => {
    categorySuggestAbortRef.current?.abort();
    const controller = new AbortController();
    categorySuggestAbortRef.current = controller;
    setCategorySuggestLoading(true);
    fetchCategorySuggestions(query, filters || undefined, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        if (res.success) setCategorySuggestions(res.category_suggestions || []);
      })
      .catch(() => {
        /* aborted or failed — advisory only, ignore */
      })
      .finally(() => {
        // Only clear loading if this is still the current (un-superseded) match.
        if (categorySuggestAbortRef.current === controller) {
          setCategorySuggestLoading(false);
        }
      });
  }, []);

  const runSearch = useCallback(async (
    overrideQuery?: string,
    overrideBoosts?: CollaboratorBoosts,
    overrideFilters?: string,
  ) => {
    const query = (overrideQuery ?? searchInput).trim();
    if (!query || isSearching) return;

    setIsSearching(true);
    setSearchError(null);
    setLastQuery(query);
    // New search → drop stale narrow-filter chips and cancel any pending match.
    categorySuggestAbortRef.current?.abort();
    setCategorySuggestions([]);
    setCategorySuggestLoading(false);

    try {
      const boostsToUse = overrideBoosts ?? activeBoosts;
      const baseFilters = (overrideFilters ?? activeFilters).trim();
      // Layer any filter nodes scoped to the current focus on top of the
      // global filters. Searches inherit constraints attached to the focused
      // node (or any ancestor, e.g. a root-level inquiry-wide filter).
      const scoped = computeScopedFilters(
        inquiryGraphRef.current.nodes,
        focusedNodeIdRef.current
      );
      const filtersToUse = andOData(baseFilters, scoped.odata).trim();
      const hasBoosts = boostsToUse && Object.values(boostsToUse).some((v) =>
        Array.isArray(v) ? v.length > 0 : Boolean(v)
      );
      const top10 = industryTop10Ref.current;
      const result = await productSemanticSearch(
        query,
        36,
        filtersToUse || undefined,
        hasBoosts ? boostsToUse : undefined,
        {
          industryTop10Codes: top10.codes.length ? top10.codes : undefined,
          industryName: top10.name || undefined,
          industryFilterNames: top10.names.length ? top10.names : undefined,
        }
      );
      if (!result.success) {
        setSearchError(result.error || 'Search failed');
        setProducts([]);
        setDisplayData({ ...EMPTY_DISPLAY, type: 'product_display', heading: 'No results' });
        return;
      }
      // Search is back — let the narrow-filter chips load in the background.
      loadCategorySuggestions(query, filtersToUse);
      if (activeLaneIdRef.current) {
        setInquiryGraph((prev) => {
          const lane = prev.nodes[activeLaneIdRef.current!];
          if (!lane || lane.kind !== 'lane') return prev;
          return {
            ...prev,
            nodes: {
              ...prev.nodes,
              [lane.id]: { ...lane, laneStatus: 'done' },
            },
          };
        });
      }
      const found = result.products || [];
      setProducts(found);
      setVisibleProducts(found);
      setHighlightedStyleCodes([]); // clear highlights on each new search
      setDisplayData({
        type: 'product_display',
        heading: found.length
          ? `${found.length} results for “${query}”`
          : `No results for “${query}”`,
        products: found,
        facets: result.facets || [],
      });
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsSearching(false);
    }
  }, [searchInput, isSearching, activeBoosts, activeFilters, loadCategorySuggestions]);

  const inquiryTopicLabel = useMemo(() => {
    if (isInquiring) return 'Pending inquiry';
    if (products.length > 0 && lastQuery) {
      const n = visibleProducts.length > 0 ? visibleProducts.length : products.length;
      return `${lastQuery} (${n})`;
    }
    if (lastQuery) return lastQuery;
    return 'Explore';
  }, [isInquiring, products.length, lastQuery, visibleProducts.length]);

  // Label shown in TOPIC + sent to the agent — follows the focused graph node
  // when the user clicks a topic, note, or SKU; root uses work-panel state.
  const agentFocusLabel = useMemo(() => {
    const n = inquiryGraph.nodes[focusedNodeId];
    if (!n || n.kind === 'root') return inquiryTopicLabel;
    if (n.kind === 'customer') return n.label;
    if (n.kind === 'lane') {
      const st = n.laneStatus || 'pending';
      return `${n.label} (${st})`;
    }
    if (n.kind === 'topic') {
      const count = n.productCount ?? 0;
      return count > 0 ? `${n.label} (${count})` : n.label;
    }
    if (n.kind === 'sku') return n.sku || n.label;
    return n.label;
  }, [inquiryGraph.nodes, focusedNodeId, inquiryTopicLabel]);

  // Per-node cost + counts. Own metrics come from the node's product
  // metadata; parents inherit the combined totals of all descendants.
  const nodeRollups = useMemo(() => {
    const map: Record<string, NodeRollup> = {};
    const nodes = inquiryGraph.nodes;

    const ownOf = (n: InquiryNode): { p: number; i: number; c: number } => {
      // A bookmarked SKU node represents exactly one product; its cost rolls up
      // to its parent. "items" is intentionally 1-per-product for now — it will
      // later map to number-of-wearers (user-editable).
      if (n.kind === 'sku') {
        return { p: 1, i: 1, c: n.stdCost || 0 };
      }
      const prods = n.products || [];
      if (prods.length > 0) {
        let c = 0;
        for (const pr of prods) {
          c += pr.stdCost || 0;
        }
        return { p: prods.length, i: prods.length, c };
      }
      return { p: 0, i: 0, c: 0 };
    };

    const visited = new Set<string>();
    const compute = (id: string): NodeRollup => {
      if (map[id]) return map[id];
      const n = nodes[id];
      if (!n) {
        return {
          ownProducts: 0,
          ownItems: 0,
          ownCost: 0,
          numProducts: 0,
          numItems: 0,
          estCost: 0,
        };
      }
      if (visited.has(id)) {
        const own = ownOf(n);
        return {
          ownProducts: own.p,
          ownItems: own.i,
          ownCost: own.c,
          numProducts: own.p,
          numItems: own.i,
          estCost: own.c,
        };
      }
      visited.add(id);
      const own = ownOf(n);
      let numProducts = own.p;
      let numItems = own.i;
      let estCost = own.c;
      for (const cid of n.childIds) {
        if (!nodes[cid]) continue;
        if (nodes[cid].kind === 'filter') continue; // filters carry no cost/count
        const child = compute(cid);
        numProducts += child.numProducts;
        numItems += child.numItems;
        estCost += child.estCost;
      }
      const rollup: NodeRollup = {
        ownProducts: own.p,
        ownItems: own.i,
        ownCost: own.c,
        numProducts,
        numItems,
        estCost,
      };
      map[id] = rollup;
      return rollup;
    };

    for (const id of Object.keys(nodes)) compute(id);
    return map;
  }, [inquiryGraph.nodes]);

  const chatContext = useMemo((): CollaboratorChatContext => {
    const nodes = inquiryGraph.nodes;
    const focused = nodes[focusedNodeId] || nodes[inquiryGraph.rootId];

    // Ancestor chain (focused → root) for history scoping.
    const ancestorIds = new Set<string>();
    let cur: InquiryNode | undefined = focused;
    while (cur) {
      ancestorIds.add(cur.id);
      cur = cur.parentId ? nodes[cur.parentId] : undefined;
    }

    const mapNodeProducts = (list: NodeProduct[]): CollaboratorContextProduct[] =>
      list.slice(0, 60).map((p) => ({
        sku: p.sku,
        name: p.name,
        class_description: p.classDescription,
        sub_class: p.subClass,
        std_cost: p.stdCost,
        variant_count: p.variantCount,
      }));

    const mapOnScreen = (list: ProductItem[]): CollaboratorContextProduct[] =>
      list.slice(0, 40).map((p) => {
        const np = toNodeProduct(p);
        return {
          sku: np.sku,
          name: np.name,
          class_description: np.classDescription,
          sub_class: np.subClass,
          std_cost: np.stdCost,
          variant_count: np.variantCount,
        };
      });

    // The focused node carries FULL product detail. Resolution order:
    //   1) the node's own bookmarked products,
    //   2) the set the LAST Inquire ran on (when this turn is a follow-up to an
    //      inquiry on the focused node) — keeps the same inquiry context even
    //      when a free-text search never spawned a topic node,
    //   3) the on-screen results for a topic/lane node.
    let focusProducts: CollaboratorContextProduct[] = [];
    if (focused) {
      const lastInq = lastInquireRef.current;
      if (focused.products && focused.products.length > 0) {
        focusProducts = mapNodeProducts(focused.products);
      } else if (
        lastInq &&
        lastInq.nodeId === focused.id &&
        lastInq.products.length > 0
      ) {
        focusProducts = mapOnScreen(lastInq.products);
      } else if (
        (focused.kind === 'topic' || focused.kind === 'lane') &&
        (visibleProducts.length > 0 || products.length > 0)
      ) {
        focusProducts = mapOnScreen(
          visibleProducts.length > 0 ? visibleProducts : products
        );
      }
    }

    const focusRollup = nodeRollups[focused?.id || ''] || {
      numProducts: 0,
      numItems: 0,
      estCost: 0,
    };

    // Inherited wants/constraints — walk ancestors (excluding the focused
    // node) so children see their parents' qualitative requirements.
    const inheritedWants: string[] = [];
    const inheritedConstraints: string[] = [];
    if (focused?.parentId) {
      const seenW = new Set<string>();
      const seenC = new Set<string>();
      let anc: InquiryNode | undefined = nodes[focused.parentId];
      while (anc) {
        for (const w of anc.wants || []) {
          if (!seenW.has(w)) {
            seenW.add(w);
            inheritedWants.push(w);
          }
        }
        for (const c of anc.constraints || []) {
          if (!seenC.has(c)) {
            seenC.add(c);
            inheritedConstraints.push(c);
          }
        }
        anc = anc.parentId ? nodes[anc.parentId] : undefined;
      }
    }

    const focus_node: CollaboratorFocusNode | undefined = focused
      ? {
          id: focused.id,
          label: focused.label,
          kind: focused.kind,
          class_description: focused.classDescription,
          num_products: focusRollup.numProducts,
          num_items: focusRollup.numItems,
          est_cost: Math.round(focusRollup.estCost * 100) / 100,
          products: focusProducts,
          wants: focused.wants && focused.wants.length ? focused.wants : undefined,
          constraints:
            focused.constraints && focused.constraints.length
              ? focused.constraints
              : undefined,
          inherited_wants: inheritedWants.length ? inheritedWants : undefined,
          inherited_constraints: inheritedConstraints.length
            ? inheritedConstraints
            : undefined,
        }
      : undefined;

    // Every OTHER node → compact summary (counts + cost + status + reqs).
    const other_nodes: CollaboratorNodeSummary[] = Object.values(nodes)
      .filter((n) => n.id !== focused?.id && n.kind !== 'root')
      .map((n) => {
        const r = nodeRollups[n.id] || {
          numProducts: 0,
          numItems: 0,
          estCost: 0,
        };
        return {
          id: n.id,
          label: n.label,
          kind: n.kind,
          status: n.laneStatus,
          num_products: r.numProducts,
          num_items: r.numItems,
          est_cost: Math.round(r.estCost * 100) / 100,
          wants: n.wants && n.wants.length ? n.wants : undefined,
          constraints:
            n.constraints && n.constraints.length ? n.constraints : undefined,
        };
      });

    // History scoped to focused node + ancestors. Wider window than before so
    // follow-ups carry full thread context, and we ALWAYS include the most
    // recent completed Inquire analysis even if it fell outside the window —
    // follow-ups like "which is warmest?" must be able to reference it.
    const scopedMsgs = chatMessages.filter(
      (m) => m.isCompleted && (!m.nodeId || ancestorIds.has(m.nodeId))
    );
    const windowed = scopedMsgs.slice(-20);
    const lastInquire = [...scopedMsgs]
      .reverse()
      .find((m) => m.isInquire && m.role === 'agent' && m.content.trim().length > 0);
    const historyMsgs =
      lastInquire && !windowed.some((m) => m.id === lastInquire.id)
        ? [lastInquire, ...windowed]
        : windowed;
    const history = historyMsgs.map((m) => ({ role: m.role, content: m.content }));

    return {
      topic: agentFocusLabel,
      phase: isInquiring
        ? 'pending_inquire'
        : products.length > 0
        ? 'results'
        : 'explore',
      has_results: products.length > 0,
      product_count: visibleProducts.length || products.length,
      focus_node,
      other_nodes,
      history,
    };
  }, [
    agentFocusLabel,
    isInquiring,
    products,
    visibleProducts,
    inquiryGraph,
    focusedNodeId,
    nodeRollups,
    chatMessages,
  ]);

  const clearWorkAndChat = useCallback(() => {
    setSearchInput('');
    setLastQuery('');
    setProducts([]);
    setVisibleProducts([]);
    setDisplayData(EMPTY_DISPLAY);
    setActiveBoosts({});
    setActiveFilterTags([]);
    setActiveFilters('');
    setHighlightedStyleCodes([]);
    setSearchError(null);
    setChatMessages([]);
    setChatInput('');
    setFocusedNodeId(inquiryGraph.rootId);
    currentTopicIdRef.current = null;
    activeLaneIdRef.current = null;
    lastSearchSignatureRef.current = '';
    setExpandedTopicIds(new Set());
    setFocusGraphExpanded(false);
  }, [inquiryGraph.rootId]);

  /** Full graph wipe — used by navbar Clear chat (drops pinned nodes too). */
  const resetCollaboratorSession = useCallback(() => {
    clearWorkAndChat();
    industryTop10Ref.current = { codes: [], name: '', names: [] };
    const fresh = emptyInquiryGraph();
    setInquiryGraph(fresh);
    setFocusedNodeId(INQUIRY_ROOT_ID);
    try {
      localStorage.removeItem(COLLAB_INQUIRY_GRAPH_KEY);
    } catch {
      /* ignore */
    }
  }, [clearWorkAndChat]);

  useImperativeHandle(ref, () => ({ resetSession: resetCollaboratorSession }), [
    resetCollaboratorSession,
  ]);

  /** Clear topic — clears the work panel + chat. The inquiry graph is kept,
   *  pruning only transient `topic` nodes that hold no bookmarked products and
   *  have no kept descendants (structure + bookmarks always survive). */
  const clearInquiry = useCallback(() => {
    clearWorkAndChat();
    setInquiryGraph((prev) => {
      const keep = new Set<string>();
      const hasValue = (id: string): boolean => {
        const n = prev.nodes[id];
        if (!n) return false;
        // Structural nodes + any node carrying bookmarks are always worth
        // keeping; transient topic/note nodes survive only if a descendant is.
        const ownValue =
          n.kind === 'root' ||
          n.kind === 'customer' ||
          n.kind === 'lane' ||
          n.kind === 'class' ||
          n.kind === 'sku' ||
          (n.products?.length || 0) > 0;
        const childValue = n.childIds.some((c) => hasValue(c));
        if (ownValue || childValue) {
          keep.add(id);
          return true;
        }
        return false;
      };
      hasValue(prev.rootId);
      keep.add(prev.rootId);
      const nodes: Record<string, InquiryNode> = {};
      keep.forEach((id) => {
        const n = prev.nodes[id];
        if (!n) return;
        nodes[id] = { ...n, childIds: n.childIds.filter((c) => keep.has(c)) };
      });
      return { ...prev, nodes };
    });
  }, [clearWorkAndChat]);

  const updateLaneStatus = useCallback((laneId: string, status: CollaboratorLaneStatus) => {
    setInquiryGraph((prev) => {
      const lane = prev.nodes[laneId];
      if (!lane || lane.kind !== 'lane') return prev;
      return {
        ...prev,
        nodes: { ...prev.nodes, [laneId]: { ...lane, laneStatus: status } },
      };
    });
  }, []);

  const skipLane = useCallback(
    (laneId: string) => {
      updateLaneStatus(laneId, 'skipped');
      if (activeLaneIdRef.current === laneId) activeLaneIdRef.current = null;
      const root = inquiryGraph.nodes[inquiryGraph.rootId];
      const nextPending = root?.childIds
        .map((cid) => inquiryGraph.nodes[cid])
        .find((n) => n?.kind === 'lane' && n.laneStatus === 'pending');
      if (nextPending) setFocusedNodeId(nextPending.id);
      else setFocusedNodeId(inquiryGraph.rootId);
    },
    [inquiryGraph, updateLaneStatus]
  );

  // Apply a successful prospect bootstrap to the graph + work panel + chat.
  const applyProspectBootstrap = useCallback(
    async (bootstrap: CollaboratorProspectBootstrapResponse) => {
      if (!bootstrap.prospect) return;
      clearWorkAndChat();
      industryTop10Ref.current = {
        codes: bootstrap.industryTop10Codes || [],
        name: bootstrap.approvedIndustry || bootstrap.prospect.industry,
        names: [bootstrap.approvedIndustry || bootstrap.prospect.industry].filter(Boolean),
      };
      const graph = buildGraphFromProspectBootstrap(bootstrap.prospect, bootstrap);
      setInquiryGraph(graph);
      setFocusedNodeId(INDUSTRY_FILTER_NODE_ID);
      setActiveFilters(bootstrap.filters || '');
      setActiveFilterTags(bootstrap.tags || []);
      setActiveBoosts(bootstrap.boosts || {});
      if (bootstrap.agentMessage) {
        setChatMessages([
          {
            id: `agent-prospect-${Date.now()}`,
            role: 'agent',
            content: bootstrap.agentMessage,
            timestamp: new Date(),
            isCompleted: true,
          },
        ]);
      }
      setFocusGraphExpanded(true);
      const starter = bootstrap.starterQuery || `${bootstrap.approvedIndustry} uniforms`;
      setSearchInput(starter);
      await runSearch(starter, bootstrap.boosts || {}, bootstrap.filters || '');
    },
    [clearWorkAndChat, runSearch]
  );

  // Bootstrap from a structured prospect, a partial prospect, or NL text.
  // Used by the chat NL-kickoff path; failures fall through silently so the
  // agent's normal reply still renders.
  const bootstrapProspectFrom = useCallback(
    async (
      input: CollaboratorProspect | Partial<CollaboratorProspect> | { text: string }
    ): Promise<boolean> => {
      let bootstrap: CollaboratorProspectBootstrapResponse;
      try {
        bootstrap = await collaboratorProspectBootstrap(input);
      } catch (err) {
        console.warn('Prospect bootstrap request failed', err);
        return false;
      }
      if (!bootstrap.success || !bootstrap.prospect) {
        console.warn('Prospect bootstrap failed:', bootstrap.error);
        return false;
      }
      await applyProspectBootstrap(bootstrap);
      return true;
    },
    [applyProspectBootstrap]
  );

  // ─────────────────────────────────────────────────────────────────────
  // Inquiry Focus Graph — helpers + auto-build effects.
  // ─────────────────────────────────────────────────────────────────────
  const buildHighlightSnapshot = useCallback((): CollaboratorBookmark['highlightSnapshot'] => {
    const highlightSet = new Set(highlightedStyleCodes.map(normStyleToken).filter(Boolean));
    const matches = (p: ProductItem) => {
      const b = normStyleToken(p.base_sku);
      const ps = normStyleToken(p.partialSku_OPIM);
      const sc = normStyleToken(p.attributes?.style_code);
      return highlightSet.has(b) || highlightSet.has(ps) || highlightSet.has(sc);
    };
    const source = visibleProducts.length > 0 ? visibleProducts : products;
    const subset = highlightSet.size > 0 ? source.filter(matches) : source.slice(0, 25);
    return subset.map((p) => ({
      sku: p.base_sku || p.attributes?.style_code || p.partialSku_OPIM || '',
      name: p.name || '',
      image: p.primary_image || p.color_variants?.[0]?.image_url || '',
    }));
  }, [highlightedStyleCodes, visibleProducts, products]);

  const upsertTopicNode = useCallback(
    (opts: {
      label: string;
      query: string;
      boosts: CollaboratorBoosts;
      filters: string;
      filterTags: CollaboratorFilterTag[];
      highlightedStyleCodes: string[];
      productCount: number;
      highlightSnapshot: Array<{ sku: string; name?: string; image?: string }>;
    }): string => {
      let topicId = `topic-${slugify(opts.query) || `t${Date.now()}`}`;
      setInquiryGraph((prev) => {
        // Clicking a green mainline category (lane) runs its search but must
        // NOT spawn a child topic node — that just clutters the graph with a
        // duplicate. Instead the lane node itself holds the search results.
        const activeLane =
          activeLaneIdRef.current ? prev.nodes[activeLaneIdRef.current] : undefined;
        if (activeLane && activeLane.kind === 'lane') {
          topicId = activeLane.id;
          const updatedLane: InquiryNode = {
            ...activeLane,
            query: opts.query,
            boosts: opts.boosts,
            filters: opts.filters,
            filterTags: opts.filterTags,
            highlightedStyleCodes: opts.highlightedStyleCodes,
            productCount: opts.productCount,
            highlightSnapshot: opts.highlightSnapshot,
            laneStatus: 'done',
          };
          return {
            ...prev,
            nodes: { ...prev.nodes, [activeLane.id]: updatedLane },
          };
        }

        const parentId =
          activeLaneIdRef.current && prev.nodes[activeLaneIdRef.current]
            ? activeLaneIdRef.current
            : prev.rootId;
        const id =
          parentId.startsWith('lane-')
            ? `${parentId}--topic-${slugify(opts.query) || `t${Date.now()}`}`
            : `topic-${slugify(opts.query) || `t${Date.now()}`}`;
        topicId = id;
        const existing = prev.nodes[id];
        const updated: InquiryNode = {
          ...(existing ?? {
            id,
            kind: 'topic',
            label: opts.label,
            parentId,
            childIds: [],
            createdAt: Date.now(),
          }),
          id,
          kind: 'topic',
          label: opts.label || existing?.label || opts.query,
          query: opts.query,
          boosts: opts.boosts,
          filters: opts.filters,
          filterTags: opts.filterTags,
          highlightedStyleCodes: opts.highlightedStyleCodes,
          productCount: opts.productCount,
          highlightSnapshot: opts.highlightSnapshot,
          pinned: existing?.pinned,
          childIds: existing?.childIds ?? [],
        };
        const nodes = { ...prev.nodes, [id]: updated };
        if (!existing) {
          const parent = nodes[parentId];
          if (parent) {
            nodes[parentId] = {
              ...parent,
              childIds: [...parent.childIds, id],
            };
          }
        }
        return { ...prev, nodes };
      });
      return topicId;
    },
    []
  );

  // The highlighted products (full records), drawn from the currently visible
  // result set and deduped by style token. The bookmark path snapshots both the
  // compact SKU metadata and the full item from these.
  const pickHighlightedItems = useCallback((): ProductItem[] => {
    const highlightSet = new Set(
      highlightedStyleCodes.map(normStyleToken).filter(Boolean)
    );
    if (highlightSet.size === 0) return [];
    const source = visibleProducts.length > 0 ? visibleProducts : products;
    const out: ProductItem[] = [];
    const seen = new Set<string>();
    for (const p of source) {
      const tok = productStyleToken(p);
      if (!tok || !highlightSet.has(tok) || seen.has(tok)) continue;
      seen.add(tok);
      out.push(p);
    }
    return out;
  }, [highlightedStyleCodes, visibleProducts, products]);

  // Primary bookmark entry point used by the toolbar + node-panel buttons.
  // Resolves the target node for the CURRENT search and — crucially — lazily
  // creates the topic node here (not on search) so that a free-text search only
  // produces a graph node once products are actually bookmarked onto it.
  // Target precedence: an explicitly focused working node → the active lane →
  // an already-existing topic for this query → otherwise create a new topic.
  // Node creation + SKU children are applied in a single state update so the
  // freshly created node is guaranteed present when its children are attached.
  const handleBookmark = useCallback(() => {
    const picked = pickHighlightedItems();
    if (picked.length === 0) return;

    const graph = inquiryGraphRef.current;
    const focused = graph.nodes[focusedNodeIdRef.current];
    const laneId = activeLaneIdRef.current;
    const q = (lastQuery || searchInput || '').trim();
    const qSlug = slugify(q);

    // Highlighted products always belong to the CURRENT search, so the target
    // must track the current query — not whatever node happens to still be
    // focused. Precedence:
    //   1. an active lane search → results live on the lane node itself;
    //   2. the focused topic ONLY if its query matches the current search
    //      (i.e. the user is still curating that same search);
    //   3. otherwise find-or-create a topic keyed to the current query, so a
    //      new search yields a NEW node rather than piling onto the old one.
    const focusedMatchesQuery =
      !!focused &&
      focused.kind === 'topic' &&
      !!qSlug &&
      slugify(focused.query || focused.label || '') === qSlug;

    let targetId: string;
    if (laneId && graph.nodes[laneId]?.kind === 'lane') {
      targetId = laneId;
    } else if (focusedMatchesQuery) {
      targetId = focused!.id;
    } else {
      targetId = `topic-${qSlug || `t${Date.now()}`}`;
    }

    const snap = buildHighlightSnapshot();
    setInquiryGraph((prev) => {
      const nodes = { ...prev.nodes };
      // Lazily create the topic node for this search if it doesn't exist yet.
      if (!nodes[targetId]) {
        nodes[targetId] = {
          id: targetId,
          kind: 'topic',
          label: q || 'Results',
          parentId: prev.rootId,
          childIds: [],
          createdAt: Date.now(),
          query: q,
          boosts: { ...activeBoosts },
          filters: activeFilters,
          filterTags: [...activeFilterTags],
          highlightedStyleCodes: [...highlightedStyleCodes],
          productCount: products.length,
          highlightSnapshot: snap,
        };
        const root = nodes[prev.rootId];
        if (root) {
          nodes[prev.rootId] = {
            ...root,
            childIds: [...root.childIds, targetId],
          };
        }
      }
      const parent = nodes[targetId];
      if (!parent) return prev;
      const childIds = [...parent.childIds];
      const existingTokens = new Set(
        childIds
          .map((cid) => nodes[cid])
          .filter((c) => c?.kind === 'sku')
          .map((c) => normStyleToken(c!.sku))
          .filter(Boolean)
      );
      for (const p of picked) {
        const np = toNodeProduct(p);
        const norm = normStyleToken(np.sku);
        if (!norm || existingTokens.has(norm)) continue;
        existingTokens.add(norm);
        const skuId = `${targetId}--sku-${norm}`;
        nodes[skuId] = {
          id: skuId,
          kind: 'sku',
          label: np.sku,
          parentId: targetId,
          childIds: [],
          sku: np.sku,
          stdCost: np.stdCost,
          variantCount: np.variantCount,
          image: np.image,
          item: p,
          createdAt: Date.now(),
        };
        childIds.push(skuId);
      }
      nodes[targetId] = { ...parent, childIds };
      return { ...prev, nodes };
    });

    currentTopicIdRef.current = targetId;
    setExpandedTopicIds((prev) => {
      const next = new Set(prev);
      next.add(targetId);
      return next;
    });
    setFocusedNodeId(targetId);
  }, [
    pickHighlightedItems,
    lastQuery,
    searchInput,
    activeBoosts,
    activeFilters,
    activeFilterTags,
    highlightedStyleCodes,
    products.length,
    buildHighlightSnapshot,
  ]);

  const removeNode = useCallback((id: string) => {
    // Deleting the top of the inquiry (the customer/root) ends the session
    // and resets to a clean slate so a new prospect can be started.
    if (id === inquiryGraph.rootId) {
      resetCollaboratorSession();
      return;
    }
    setInquiryGraph((prev) => {
      if (id === prev.rootId) return prev;
      const target = prev.nodes[id];
      if (!target) return prev;
      const toRemove = new Set<string>();
      const stack = [id];
      while (stack.length) {
        const n = stack.pop()!;
        const node = prev.nodes[n];
        if (!node) continue;
        toRemove.add(n);
        for (const c of node.childIds) stack.push(c);
      }
      const nodes: Record<string, InquiryNode> = {};
      for (const [k, v] of Object.entries(prev.nodes)) {
        if (toRemove.has(k)) continue;
        nodes[k] = { ...v, childIds: v.childIds.filter((c) => !toRemove.has(c)) };
      }
      return { ...prev, nodes };
    });
    setFocusedNodeId((curr) => {
      const root = inquiryGraph.rootId;
      return curr === id ? root : curr;
    });
    if (currentTopicIdRef.current === id) currentTopicIdRef.current = null;
  }, [inquiryGraph.rootId, resetCollaboratorSession]);

  const renameNode = useCallback((id: string, label: string) => {
    const trimmed = (label || '').trim();
    if (!trimmed) return;
    setInquiryGraph((prev) => {
      const n = prev.nodes[id];
      if (!n) return prev;
      return { ...prev, nodes: { ...prev.nodes, [id]: { ...n, label: trimmed } } };
    });
  }, []);

  // ── Wants / constraints (progressive qualitative summary per node) ──
  const addNodeReq = useCallback(
    (id: string, field: 'wants' | 'constraints', text: string) => {
      const trimmed = (text || '').trim();
      if (!trimmed) return;
      setInquiryGraph((prev) => {
        const n = prev.nodes[id];
        if (!n) return prev;
        const list = n[field] || [];
        if (list.some((x) => x.toLowerCase() === trimmed.toLowerCase())) {
          return prev;
        }
        return {
          ...prev,
          nodes: { ...prev.nodes, [id]: { ...n, [field]: [...list, trimmed] } },
        };
      });
    },
    []
  );

  const removeNodeReq = useCallback(
    (id: string, field: 'wants' | 'constraints', text: string) => {
      setInquiryGraph((prev) => {
        const n = prev.nodes[id];
        if (!n) return prev;
        const list = (n[field] || []).filter((x) => x !== text);
        return {
          ...prev,
          nodes: { ...prev.nodes, [id]: { ...n, [field]: list } },
        };
      });
    },
    []
  );

  // Agent-maintained summary: REPLACE the node's wants/constraints (lets the
  // agent relax or delete entries when it detects a contradiction).
  const applyNodeSummary = useCallback(
    (id: string, summary: { wants?: string[]; constraints?: string[] }) => {
      const clean = (arr?: string[]) =>
        Array.isArray(arr)
          ? Array.from(
              new Set(
                arr
                  .map((s) => (typeof s === 'string' ? s.trim() : ''))
                  .filter(Boolean)
              )
            ).slice(0, 12)
          : undefined;
      const wants = clean(summary.wants);
      const constraints = clean(summary.constraints);
      if (wants === undefined && constraints === undefined) return;
      setInquiryGraph((prev) => {
        const n = prev.nodes[id];
        if (!n) return prev;
        const next: InquiryNode = { ...n };
        if (wants !== undefined) next.wants = wants;
        if (constraints !== undefined) next.constraints = constraints;
        return { ...prev, nodes: { ...prev.nodes, [id]: next } };
      });
    },
    []
  );

  /** Topic node for the active search (used as the default bookmark target). */
  const activeTopicNodeId = useMemo(() => {
    if (currentTopicIdRef.current && inquiryGraph.nodes[currentTopicIdRef.current]) {
      return currentTopicIdRef.current;
    }
    if (lastQuery) {
      const slug = slugify(lastQuery);
      // Root-scoped id (fast path).
      if (inquiryGraph.nodes[`topic-${slug}`]) return `topic-${slug}`;
      // Lane-scoped topics live under `lane-…--topic-<slug>`, so scan for any
      // topic node whose query/label matches the active search.
      const match = Object.values(inquiryGraph.nodes).find(
        (n) =>
          n.kind === 'topic' &&
          slugify(n.query || n.label || '') === slug
      );
      if (match) return match.id;
    }
    const focused = inquiryGraph.nodes[focusedNodeId];
    if (focused?.kind === 'topic') return focused.id;
    return null;
  }, [lastQuery, inquiryGraph.nodes, focusedNodeId]);

  const addChildNode = useCallback((parentId: string, label: string) => {
    const trimmed = (label || '').trim();
    if (!trimmed) return;
    setInquiryGraph((prev) => {
      const parent = prev.nodes[parentId];
      if (!parent) return prev;
      const id = `note-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const node: InquiryNode = {
        id,
        kind: 'note',
        label: trimmed,
        parentId,
        childIds: [],
        createdAt: Date.now(),
      };
      return {
        ...prev,
        nodes: {
          ...prev.nodes,
          [id]: node,
          [parentId]: { ...parent, childIds: [...parent.childIds, id] },
        },
      };
    });
  }, []);

  // Add one draggable `filter` node per resolved constraint as a child of
  // `targetId` (dedup by term). The node applies to its parent + descendants
  // and is excluded from cost/count rollups.
  const createFilterNodes = useCallback(
    (targetId: string, filters: CollaboratorConstraintFilter[]) => {
      if (!filters.length) return;
      setInquiryGraph((prev) => {
        const parent = prev.nodes[targetId] || prev.nodes[prev.rootId];
        if (!parent) return prev;
        const pid = parent.id;
        const nodes = { ...prev.nodes };
        const existingTerms = new Set(
          parent.childIds
            .map((cid) => prev.nodes[cid])
            .filter((c): c is InquiryNode => Boolean(c) && c!.kind === 'filter')
            .map((c) => (c.filterTerm || c.label).toLowerCase())
        );
        const newChildIds: string[] = [];
        for (const cf of filters) {
          const term = (cf.term || cf.label || '').trim();
          if (!term || !cf.odata || existingTerms.has(term.toLowerCase())) continue;
          existingTerms.add(term.toLowerCase());
          const id = `filter-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          nodes[id] = {
            id,
            kind: 'filter',
            label: cf.label || term,
            parentId: pid,
            childIds: [],
            filterOData: cf.odata,
            filterTerm: term,
            filterConfidence: cf.confidence,
            filterTag: cf.tag || null,
            createdAt: Date.now(),
          };
          newChildIds.push(id);
        }
        if (!newChildIds.length) return prev;
        nodes[pid] = { ...parent, childIds: [...parent.childIds, ...newChildIds] };
        return { ...prev, nodes };
      });
    },
    []
  );

  // Confirm-first apply for resolved constraint filters: attach a draggable
  // filter node to the focused working node (fallback: active topic / root),
  // then re-run the current search with the new clause layered in. The scoped
  // OData is passed explicitly so the just-added node takes effect immediately
  // (the graph ref hasn't re-rendered yet).
  const applyConstraintFilters = useCallback(
    async (filters: CollaboratorConstraintFilter[]) => {
      const valid = filters.filter((f) => f && f.odata);
      if (!valid.length) return;
      const focused = inquiryGraph.nodes[focusedNodeId];
      const targetId =
        focused &&
        focused.kind !== 'customer' &&
        focused.kind !== 'root' &&
        focused.kind !== 'sku'
          ? focused.id
          : activeTopicNodeId || inquiryGraph.rootId;
      createFilterNodes(targetId, valid);
      let override = activeFilters;
      for (const f of valid) override = andOData(override, f.odata);
      const q = lastQuery || searchInput.trim();
      if (q) await runSearch(q, activeBoosts, override);
    },
    [
      inquiryGraph.nodes,
      focusedNodeId,
      activeTopicNodeId,
      inquiryGraph.rootId,
      activeFilters,
      lastQuery,
      searchInput,
      runSearch,
      activeBoosts,
      createFilterNodes,
    ]
  );

  // Drag-to-reparent a filter node onto a new target (root = inquiry-wide).
  // Re-runs the current search so the new scope takes effect immediately.
  const reparentFilterNode = useCallback(
    (childId: string, newParentId: string) => {
      let nextNodes: Record<string, InquiryNode> | null = null;
      setInquiryGraph((prev) => {
        const child = prev.nodes[childId];
        const newParent = prev.nodes[newParentId];
        if (!child || child.kind !== 'filter' || !newParent) return prev;
        if (child.parentId === newParentId) return prev;
        const nodes = { ...prev.nodes };
        if (child.parentId && nodes[child.parentId]) {
          const old = nodes[child.parentId];
          nodes[old.id] = {
            ...old,
            childIds: old.childIds.filter((cid) => cid !== childId),
          };
        }
        nodes[newParentId] = {
          ...newParent,
          childIds: [...newParent.childIds, childId],
        };
        nodes[childId] = { ...child, parentId: newParentId };
        nextNodes = nodes;
        return { ...prev, nodes };
      });
      // Re-run the current search against the NEW scope (graph ref lags a tick).
      if (nextNodes) {
        const scoped = computeScopedFilters(nextNodes, focusedNodeIdRef.current);
        const q = lastQuery || searchInput.trim();
        if (q) void runSearch(q, activeBoosts, andOData(activeFilters, scoped.odata));
      }
    },
    [lastQuery, searchInput, runSearch, activeBoosts, activeFilters]
  );

  // After focusing a parent and re-running its search, surface every bookmarked
  // SKU beneath it: highlight them all (so Inquire can use the curated set with
  // one click) and inject any whose product came from a DIFFERENT search than
  // the one just loaded (so it isn't already in the result set). Same-search
  // bookmarks are already present and are simply highlighted. Recurses through
  // descendants so multi-layer bookmarks across searches all come along.
  const surfaceBookmarkedDescendants = useCallback((rootId: string) => {
    const nodes = inquiryGraphRef.current.nodes;
    const root = nodes[rootId];
    if (!root) return;
    const tokens: string[] = [];
    const seen = new Set<string>();
    const inject: ProductItem[] = [];
    const stack = [...root.childIds];
    while (stack.length) {
      const c = nodes[stack.pop()!];
      if (!c) continue;
      if (c.kind === 'sku' && c.sku) {
        const tok = normStyleToken(c.sku);
        if (tok && !seen.has(tok)) {
          seen.add(tok);
          tokens.push(c.sku);
          if (c.item) inject.push(c.item);
        }
      }
      if (c.childIds.length) stack.push(...c.childIds);
    }
    if (tokens.length === 0) return;

    if (inject.length) {
      // Prepend cross-search bookmarks (deduped) so they're visible up top.
      const mergeMissing = (list: ProductItem[]): ProductItem[] => {
        const present = new Set(list.map(productStyleToken).filter(Boolean));
        const add = inject.filter((p) => {
          const t = productStyleToken(p);
          return t && !present.has(t);
        });
        return add.length ? [...add, ...list] : list;
      };
      setProducts((prev) => mergeMissing(prev));
      setVisibleProducts((prev) => mergeMissing(prev));
      setDisplayData((prev) =>
        prev && prev.type === 'product_display'
          ? { ...prev, products: mergeMissing(prev.products || []) }
          : prev
      );
    }
    setHighlightedStyleCodes(tokens);
  }, []);

  const focusNode = useCallback(
    async (id: string) => {
      const n = inquiryGraph.nodes[id];
      if (!n) return;
      setFocusedNodeId(id);
      if (n.kind === 'topic' && n.query) {
        currentTopicIdRef.current = id;
        setActiveBoosts({ ...(n.boosts || {}) });
        setActiveFilters(n.filters || '');
        setActiveFilterTags([...(n.filterTags || [])]);
        setHighlightedStyleCodes([...(n.highlightedStyleCodes || [])]);
        setSearchInput(n.query);
        await runSearch(n.query, n.boosts, n.filters);
        surfaceBookmarkedDescendants(id);
      } else if (n.kind === 'lane' && n.searchQuery) {
        if (n.laneStatus === 'skipped') return;
        activeLaneIdRef.current = id;
        updateLaneStatus(id, 'active');
        setSearchInput(n.searchQuery);
        await runSearch(n.searchQuery, activeBoosts, activeFilters);
        surfaceBookmarkedDescendants(id);
      } else if (n.kind === 'sku' && n.sku) {
        handleStyleCodeClick(n.sku);
      } else if (n.kind === 'root' || n.kind === 'customer') {
        activeLaneIdRef.current = null;
      }
    },
    // handleStyleCodeClick is defined later in this component
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inquiryGraph.nodes, runSearch, activeBoosts, activeFilters, updateLaneStatus, surfaceBookmarkedDescendants]
  );

  // Agent-initiated focus switch — resolve by node id first, then by a
  // case-insensitive label match. Returns the resolved node id (or null).
  const focusNodeByRef = useCallback(
    (nodeId?: string, nodeLabel?: string): string | null => {
      const nodes = inquiryGraph.nodes;
      let target = nodeId && nodes[nodeId] ? nodeId : null;
      if (!target && nodeLabel) {
        const want = nodeLabel.trim().toLowerCase();
        const match = Object.values(nodes).find(
          (n) =>
            n.label.toLowerCase() === want ||
            n.label.toLowerCase().includes(want) ||
            (n.classDescription || '').toLowerCase() === want
        );
        if (match) target = match.id;
      }
      if (target) {
        void focusNode(target);
        return target;
      }
      return null;
    },
    [inquiryGraph.nodes, focusNode]
  );

  const exportNodeCsv = useCallback(
    (id: string) => {
      const n = inquiryGraph.nodes[id];
      if (!n || n.kind !== 'topic') return;
      const safe = n.label.replace(/[^A-Za-z0-9-_]+/g, '-').slice(0, 60) || 'topic';
      const bm: CollaboratorBookmark = {
        id: n.id,
        label: n.label,
        query: n.query || '',
        boosts: n.boosts || {},
        filters: n.filters || '',
        filterTags: n.filterTags || [],
        highlightedStyleCodes: n.highlightedStyleCodes || [],
        productCount: n.productCount || 0,
        highlightSnapshot: n.highlightSnapshot || [],
        createdAt: n.createdAt,
      };
      downloadBlob(`${safe}.csv`, 'text/csv;charset=utf-8', bookmarkToCsv(bm));
    },
    [inquiryGraph.nodes]
  );

  const exportNodePdf = useCallback(
    (id: string) => {
      const n = inquiryGraph.nodes[id];
      if (!n || n.kind !== 'topic') return;
      const bm: CollaboratorBookmark = {
        id: n.id,
        label: n.label,
        query: n.query || '',
        boosts: n.boosts || {},
        filters: n.filters || '',
        filterTags: n.filterTags || [],
        highlightedStyleCodes: n.highlightedStyleCodes || [],
        productCount: n.productCount || 0,
        highlightSnapshot: n.highlightSnapshot || [],
        createdAt: n.createdAt,
      };
      exportBookmarkPdf(bm);
    },
    [inquiryGraph.nodes]
  );

  // Search no longer auto-creates a topic node. A node (and its products) is
  // created lazily on bookmark instead — see `handleBookmark`. The only thing
  // we do here is update an EXISTING lane node in place with its latest search
  // results (count badge + highlights), since clicking a lane is itself a
  // search against an already-present node. Free-text searches create nothing.
  // The signature ref is hoisted above so `clearInquiry` can reset it.
  useEffect(() => {
    if (!lastQuery || products.length === 0) return;
    const signature = `${lastQuery}::${products.length}`;
    if (lastSearchSignatureRef.current === signature) return;
    lastSearchSignatureRef.current = signature;
    const laneId = activeLaneIdRef.current;
    if (laneId && inquiryGraphRef.current.nodes[laneId]?.kind === 'lane') {
      const topicId = upsertTopicNode({
        label: lastQuery,
        query: lastQuery,
        boosts: { ...activeBoosts },
        filters: activeFilters,
        filterTags: [...activeFilterTags],
        highlightedStyleCodes: [...highlightedStyleCodes],
        productCount: products.length,
        highlightSnapshot: buildHighlightSnapshot(),
      });
      currentTopicIdRef.current = topicId;
      setFocusedNodeId(topicId);
    } else {
      // Free-text search: no node yet. Bookmarking will create it.
      currentTopicIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastQuery, products.length]);

  // Topic expansion state is declared at the top of the component so that
  // `clearInquiry` can reset it. Only the toggler lives here.
  const toggleTopicExpanded = useCallback((id: string) => {
    setExpandedTopicIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const focusedNode = inquiryGraph.nodes[focusedNodeId] || inquiryGraph.nodes[inquiryGraph.rootId];
  const focusedSkuChildCount = (focusedNode.childIds || []).filter(
    (cid) => inquiryGraph.nodes[cid]?.kind === 'sku'
  ).length;
  const customerRootNode = inquiryGraph.nodes[inquiryGraph.rootId];
  const customerProspect =
    customerRootNode?.kind === 'customer' ? customerRootNode.prospect : undefined;
  const graphNodeCount = Object.values(inquiryGraph.nodes).filter(
    (n) => n.kind !== 'root' && n.kind !== 'customer'
  ).length;
  const focusedReqCount =
    (focusedNode.constraints?.length || 0) + (focusedNode.wants?.length || 0);

  // Where bookmarked (highlighted) products will land — mirrors `handleBookmark`.
  // Highlights belong to the CURRENT search, so the target tracks the query:
  //   1. an active lane search → the lane node itself;
  //   2. the focused topic ONLY if its query matches the current search;
  //   3. otherwise a topic keyed to the current query (created on bookmark if
  //      it doesn't exist yet).
  const bookmarkQuerySlug = slugify((lastQuery || searchInput || '').trim());
  const bookmarkActiveLaneId =
    activeLaneIdRef.current && inquiryGraph.nodes[activeLaneIdRef.current]?.kind === 'lane'
      ? activeLaneIdRef.current
      : null;
  const bookmarkFocusMatches =
    focusedNode.kind === 'topic' &&
    !!bookmarkQuerySlug &&
    slugify(focusedNode.query || focusedNode.label || '') === bookmarkQuerySlug;
  const bookmarkExistingTopicId =
    !bookmarkActiveLaneId && bookmarkQuerySlug && inquiryGraph.nodes[`topic-${bookmarkQuerySlug}`]
      ? `topic-${bookmarkQuerySlug}`
      : null;
  const bookmarkTargetId =
    bookmarkActiveLaneId ||
    (bookmarkFocusMatches ? focusedNode.id : null) ||
    bookmarkExistingTopicId ||
    inquiryGraph.rootId;
  // True when bookmarking will CREATE a new topic node named after the query.
  const bookmarkWillCreate =
    !bookmarkActiveLaneId && !bookmarkFocusMatches && !bookmarkExistingTopicId;
  const bookmarkTargetLabel = bookmarkWillCreate
    ? lastQuery || 'results'
    : inquiryGraph.nodes[bookmarkTargetId]?.label || 'inquiry';

  const applySuggestedAction = useCallback(
    async (action: CollaboratorSuggestedAction) => {
      if (action.type === 'focus') {
        focusNodeByRef(action.node_id, action.node_label || action.label);
        return;
      }
      if (action.type === 'note') {
        const parentId =
          focusedNode.kind === 'topic'
            ? focusedNode.id
            : activeTopicNodeId || inquiryGraph.rootId;
        addChildNode(parentId, action.label);
        return;
      }
      if (action.type === 'search' && action.search_query) {
        setSearchInput(action.search_query);
        await runSearch(action.search_query, activeBoosts, activeFilters);
        return;
      }
      if (action.type === 'industry' && action.filters) {
        // Pre-resolved industry OR filter + merged Top-10 from the backend.
        // Replace any existing industry clause, AND-merge, set chip + badges.
        const base = stripIndustryClause(activeFilters);
        const merged = base ? `${base} and ${action.filters}` : action.filters;
        setActiveFilters(merged);
        if (action.tag) {
          const newTag = action.tag;
          setActiveFilterTags((prev) => [
            ...prev.filter((t) => t.field !== 'industry'),
            newTag,
          ]);
        }
        industryTop10Ref.current = {
          codes: action.industryTop10Codes || [],
          name: action.industryName || '',
          names: action.industries && action.industries.length
            ? action.industries
            : (action.industryName ? action.industryName.split(', ').filter(Boolean) : []),
        };
        const q = lastQuery || searchInput.trim();
        if (q) await runSearch(q, activeBoosts, merged);
        return;
      }
      if (action.type === 'category' && action.filters) {
        // Pre-resolved category OR filter from the backend (class_description or
        // sub_class_description). Replace any existing filter on the SAME field,
        // AND-merge with the rest, add the chip.
        const field = action.tag?.field || 'class_description';
        const base = stripFieldEqClause(activeFilters, field);
        const merged = base ? `${base} and ${action.filters}` : action.filters;
        setActiveFilters(merged);
        if (action.tag) {
          const newTag = action.tag;
          setActiveFilterTags((prev) => [
            ...prev.filter((t) => t.field !== field),
            newTag,
          ]);
        }
        const q = lastQuery || searchInput.trim();
        if (q) await runSearch(q, activeBoosts, merged);
        return;
      }
      if (action.type === 'filter' && action.resolved?.length) {
        // Backend pre-resolved these terms to real catalog filters → apply
        // directly as scoped, draggable filter node(s). No round-trip.
        await applyConstraintFilters(action.resolved);
        return;
      }
      if (action.type === 'filter' && action.terms?.length) {
        const utterance = action.terms.join(' ');
        const result = await collaboratorChatFilter(utterance, activeBoosts, chatContext);
        if (!result.success) return;
        setActiveBoosts(result.boosts || {});
        setActiveFilterTags(result.tags || []);
        setActiveFilters(result.filters || '');
        // A `filter` action may still carry an industry (legacy) → light badges.
        if (result.industry_name || (result.industry_top_10_codes?.length ?? 0) > 0) {
          industryTop10Ref.current = {
            codes: result.industry_top_10_codes || [],
            name: result.industry_name || '',
            names: result.industry_name ? result.industry_name.split(', ').filter(Boolean) : [],
          };
        }
        const q = lastQuery || searchInput.trim() || action.terms.join(' ');
        if (q) await runSearch(q, result.boosts, result.filters);
      }
    },
    [
      focusedNode,
      activeTopicNodeId,
      inquiryGraph.rootId,
      addChildNode,
      runSearch,
      activeBoosts,
      activeFilters,
      chatContext,
      lastQuery,
      searchInput,
      focusNodeByRef,
      applyConstraintFilters,
    ]
  );

  // Send the chat-input as a filter utterance to the GPT-5.2-chat wrapper,
  // then re-run the search with the resulting boosts. Surfaces the agent's
  // reply in the chat panel and updates the filter-chip bar under the search.
  const runChatFilter = useCallback(async (
    overrideText?: string,
    prefetched?: CollaboratorFilterResponse,
  ) => {
    const text = (overrideText ?? chatInput).trim();
    if (!text || isFiltering) return;

    const now = Date.now();
    const userMsgId = `user-${now}`;
    const agentMsgId = `agent-${now}`;
    // Scope this turn to the currently focused node so the agent context can
    // replay history for the focused node + its ancestors only.
    const turnNodeId = focusedNodeId;
    setChatMessages((prev) => [
      ...prev,
      {
        id: userMsgId,
        role: 'user',
        content: text,
        timestamp: new Date(now),
        isCompleted: true,
        nodeId: turnNodeId,
      },
      {
        id: agentMsgId,
        role: 'agent',
        content: '',
        isStreaming: true,
        timestamp: new Date(now),
        isCompleted: false,
        replyToId: userMsgId,
        nodeId: turnNodeId,
      },
    ]);
    if (overrideText === undefined) setChatInput('');
    setIsFiltering(true);

    const completeAgent = (
      content: string,
      suggestedActions?: CollaboratorSuggestedAction[]
    ) =>
      setChatMessages((prev) =>
        prev.map((m) =>
          m.id === agentMsgId
            ? {
                ...m,
                content,
                isStreaming: false,
                isCompleted: true,
                suggestedActions: suggestedActions?.length
                  ? suggestedActions
                  : undefined,
              }
            : m
        )
      );

    try {
      const result = prefetched ?? await collaboratorChatFilter(text, activeBoosts, chatContext);
      if (!result.success) {
        completeAgent(result.error || 'Filter extraction failed.');
        return;
      }
      const mode = result.mode || 'filter';
      const newBoosts = result.boosts || {};
      const newTags = result.tags || [];
      const newFilters = result.filters || '';

      // Surface constraints that resolved to real catalog filters as
      // confirm-first "Apply as filter" chips (reusing the `filter` action
      // chip + handler). Nothing made up: these already cleared the backend
      // threshold. Applying one adds a draggable filter node scoped to focus.
      const actions: CollaboratorSuggestedAction[] = [
        ...(result.suggested_actions || []),
      ];
      for (const cf of result.constraint_filters || []) {
        const pct = cf.confidence < 1 ? ` (${Math.round(cf.confidence * 100)}% match)` : '';
        actions.push({
          type: 'filter',
          label: `Apply as filter: ${cf.label}${pct}`,
          terms: [cf.term],
          filters: cf.odata,
          resolved: [cf],
          tags: cf.tag ? [cf.tag] : undefined,
        });
      }

      // NL-first intake: the agent extracted prospect fields from the user's
      // intro and there is no customer yet → seed the inquiry from them. This
      // replaces the work panel + graph + chat with the prospect bootstrap.
      const hasCustomer =
        inquiryGraph.rootId === CUSTOMER_ROOT_ID ||
        Object.values(inquiryGraph.nodes).some((n) => n.kind === 'customer');
      if (result.prospect && result.prospect.company && !hasCustomer) {
        const ok = await bootstrapProspectFrom(result.prospect);
        if (ok) return;
      }

      // Progressive qualitative summary — apply the agent's wants/constraints
      // to the node this turn was scoped to (before any focus switch).
      if (result.node_summary) {
        applyNodeSummary(turnNodeId, result.node_summary);
      }

      // Agent-initiated focus switch — apply before mode handling so the
      // graph + context reflect the new focus immediately.
      if (result.set_focus) {
        focusNodeByRef(result.set_focus.node_id, result.set_focus.node_label);
      }

      if (mode === 'discuss' || mode === 'answer') {
        completeAgent(result.reply || '…', actions);
        return;
      }

      if (mode === 'search') {
        const sq = (result.search_query || text).trim();
        completeAgent(result.reply || (sq ? `Searching for “${sq}”.` : '…'), actions);
        if (sq) {
          setSearchInput(sq);
          await runSearch(sq, activeBoosts, activeFilters);
        }
        return;
      }

      // Mode = "filter" or "mixed" → update chips + re-run search.
      setActiveBoosts(newBoosts);
      setActiveFilterTags(newTags);
      setActiveFilters(newFilters);
      // Industry applied via a typed utterance → light the Top-10 badges for
      // every chosen industry (OR-combined).
      if (result.industry_name || (result.industry_top_10_codes?.length ?? 0) > 0) {
        industryTop10Ref.current = {
          codes: result.industry_top_10_codes || [],
          name: result.industry_name || '',
          names: result.industry_name ? result.industry_name.split(', ').filter(Boolean) : [],
        };
      }
      completeAgent(result.reply || 'Filters updated.', actions);
      const q = lastQuery || searchInput.trim();
      if (q) {
        await runSearch(q, newBoosts, newFilters);
      }
    } catch (err) {
      completeAgent(err instanceof Error ? err.message : 'Filter failed');
    } finally {
      setIsFiltering(false);
      scrollChatToBottom();
    }
  }, [
    chatInput,
    isFiltering,
    activeBoosts,
    activeFilters,
    lastQuery,
    searchInput,
    runSearch,
    scrollChatToBottom,
    chatContext,
    focusedNodeId,
    focusNodeByRef,
    applyNodeSummary,
    inquiryGraph,
    bootstrapProspectFrom,
  ]);

  // Remove one chip → drop the corresponding boost/penalty/term and re-run
  // the filter agent on the survivors so OData stays consistent with the
  // visible chips. For pure boost/penalty removal we can skip the round-trip
  // (no FilterAgent involvement); for term removal we re-resolve via the
  // chat-filter endpoint.
  const removeFilterTag = useCallback((tag: CollaboratorFilterTag) => {
    setActiveFilterTags((prev) => {
      const survivors = prev.filter((t) => t !== tag);

      // Industry removal → strip the `industry` ismatch clause(s) and drop the
      // Industry Top-10 badges (they no longer apply).
      if (tag.field === 'industry') {
        const nextFilters = stripIndustryClause(activeFilters);
        setActiveFilters(nextFilters);
        industryTop10Ref.current = { codes: [], name: '', names: [] };
        const q = lastQuery || searchInput.trim();
        if (q) runSearch(q, activeBoosts, nextFilters);
        return survivors;
      }

      // Category removal → strip the matching category clause from the OData
      // (top-level class_description or mid-level sub_class_description).
      if (tag.field === 'class_description' || tag.field === 'sub_class_description') {
        const nextFilters = stripFieldEqClause(activeFilters, tag.field);
        setActiveFilters(nextFilters);
        const q = lastQuery || searchInput.trim();
        if (q) runSearch(q, activeBoosts, nextFilters);
        return survivors;
      }

      // Term removal → re-resolve OData by reissuing the surviving terms.
      if (tag.field === 'term') {
        const remainingTerms = survivors
          .filter((t) => t.field === 'term')
          .map((t) => t.value || t.label)
          .filter(Boolean) as string[];
        const utterance = remainingTerms.length
          ? remainingTerms.join(' ')
          : 'clear filters';
        // Fire-and-forget; the response updates filters/boosts in place.
        collaboratorChatFilter(utterance, activeBoosts).then((res) => {
          if (!res.success) return;
          const newFilters = res.filters || '';
          setActiveFilters(newFilters);
          const q = lastQuery || searchInput.trim();
          if (q) runSearch(q, activeBoosts, newFilters);
        });
        return survivors;
      }

      // Boost/penalty removal → drop from CollaboratorBoosts, keep OData as-is.
      setActiveBoosts((prevBoosts) => {
        const next: CollaboratorBoosts = { ...prevBoosts };
        if (tag.field === 'penalty_vendors' && tag.value) {
          next.penalty_vendors = (next.penalty_vendors || []).filter((v) => v !== tag.value);
        } else if (tag.field === 'penalty_brands' && tag.value) {
          next.penalty_brands = (next.penalty_brands || []).filter((b) => b !== tag.value);
        } else {
          (next as Record<string, unknown>)[tag.field] = false;
        }
        const q = lastQuery || searchInput.trim();
        if (q) runSearch(q, next, activeFilters);
        return next;
      });
      return survivors;
    });
  }, [lastQuery, searchInput, runSearch, activeBoosts, activeFilters]);

  const clearAllFilters = useCallback(() => {
    setActiveBoosts({});
    setActiveFilterTags([]);
    setActiveFilters('');
    const q = lastQuery || searchInput.trim();
    if (q) runSearch(q, {}, '');
  }, [lastQuery, searchInput, runSearch]);

  // A category suggestion is "active" once its filter chip is on the bar.
  const activeCategoryTagFor = useCallback(
    (s: CollaboratorCategorySuggestion): CollaboratorFilterTag | undefined =>
      activeFilterTags.find(
        (t) => t.field === s.tag.field && (t.value ?? '') === (s.tag.value ?? '')
      ),
    [activeFilterTags]
  );

  // Click a category chip → apply the OR filter; click again → remove it.
  const toggleCategorySuggestion = useCallback(
    (s: CollaboratorCategorySuggestion) => {
      const existing = activeCategoryTagFor(s);
      if (existing) {
        removeFilterTag(existing);
        return;
      }
      void applySuggestedAction({
        type: 'category',
        label: s.label,
        categories: s.names,
        level: s.level,
        filters: s.filters,
        tag: s.tag,
      });
    },
    [activeCategoryTagFor, removeFilterTag, applySuggestedAction]
  );

  // ─────────────────────────────────────────────────────────────────────
  // Feedback handlers — mirrors `handleFeedbackSubmit` in ChatInterface
  // but builds conversationHistory from Collaborator's own chat messages
  // and tags the metadata with collaborator-specific context.
  // ─────────────────────────────────────────────────────────────────────
  const handleFeedbackSubmit = useCallback(
    async (
      messageId: string,
      feedback: 'positive' | 'negative',
      agentName: string,
      userMessage: string,
      agentMessage: string,
      feedbackTags?: string[],
      feedbackNotes?: string,
      allowUpdate?: boolean,
    ) => {
      const userEmail = getUserEmail();
      const messageCount = chatMessages.filter((m) => m.role === 'user').length;

      // Mirror the main chat's conversationHistory shape so feedback v2
      // analytics work against Collaborator just like Chat mode.
      const conversationHistory = chatMessages.map((m) => ({
        id: m.id,
        sender: m.role,
        content: m.content,
        timestamp: m.timestamp.toISOString(),
        agentName: m.role === 'agent' ? agentName : undefined,
        isCompleted: m.isCompleted,
        toolCalls: [] as string[],
      }));

      const metadata = {
        surface: 'product-searchlight-collaborator',
        lastQuery: lastQuery || undefined,
        activeBoosts: activeBoosts && Object.values(activeBoosts).some(
          (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v)),
        )
          ? activeBoosts
          : undefined,
        activeFilters: activeFilters || undefined,
        productCount: products.length,
      };

      await submitFeedback(
        messageId,
        feedback,
        agentName,
        userMessage,
        agentMessage,
        userEmail,
        messageCount,
        [],                  // toolCalls — Collaborator doesn't expose tool-level calls in chat
        undefined,           // a2aRouting — n/a
        feedbackTags,
        feedbackNotes,
        conversationHistory,
        undefined,           // toolCallsDetailed
        metadata,
        allowUpdate,
      );
    },
    [
      submitFeedback,
      getUserEmail,
      chatMessages,
      lastQuery,
      activeBoosts,
      activeFilters,
      products.length,
    ],
  );

  // Find the user message that prompted a given agent message — used to
  // build the `userMessage` field in the feedback payload.
  const getFeedbackContext = useCallback(
    (msg: CollaboratorChatMessage) => {
      const replyTo = msg.replyToId
        ? chatMessages.find((m) => m.id === msg.replyToId)
        : undefined;
      const userMessage = replyTo?.content ?? '';
      const existing = getFeedbackForMessage(msg.id);
      return {
        userMessage,
        existingTags: existing?.tags || [],
        existingNotes: existing?.notes || '',
      };
    },
    [chatMessages, getFeedbackForMessage],
  );

  const handleThumbsClick = useCallback(
    async (msg: CollaboratorChatMessage, feedback: 'positive' | 'negative') => {
      if (!agent) return;
      const { userMessage, existingTags, existingNotes } = getFeedbackContext(msg);
      if (feedback === 'positive') {
        await handleFeedbackSubmit(msg.id, 'positive', agent.name, userMessage, msg.content);
      } else {
        // Thumbs-down opens the modal so the user can add tags / notes —
        // identical UX to the main chat.
        setPendingFeedback({
          messageId: msg.id,
          feedback: 'negative',
          agentName: agent.name,
          userMessage,
          agentMessage: msg.content,
          notesOnly: false,
          existingTags,
          existingNotes,
          allowFeedbackSelection: true,
          allowUpdate: false,
        });
        setShowFeedbackModal(true);
      }
    },
    [agent, getFeedbackContext, handleFeedbackSubmit],
  );

  const handleCommentClick = useCallback(
    (msg: CollaboratorChatMessage) => {
      if (!agent) return;
      const { userMessage, existingTags, existingNotes } = getFeedbackContext(msg);
      const existing = getFeedbackForMessage(msg.id);
      const existingFeedbackType = existing?.feedback ?? null;
      setPendingFeedback({
        messageId: msg.id,
        feedback: existingFeedbackType ?? 'positive',
        agentName: agent.name,
        userMessage,
        agentMessage: msg.content,
        notesOnly: false,
        existingTags,
        existingNotes,
        allowFeedbackSelection: true,
        allowUpdate: !!existingFeedbackType,
      });
      setShowFeedbackModal(true);
    },
    [agent, getFeedbackContext, getFeedbackForMessage],
  );

  // Called by FeedbackModal when the user finally submits with tags/notes.
  const handleModalSubmit = useCallback(
    async (
      data: {
        messageId: string;
        feedback: 'positive' | 'negative';
        agentName: string;
        userMessage: string;
        agentMessage: string;
        feedbackTags?: string[];
        feedbackNotes?: string;
        allowUpdate?: boolean;
      },
    ) => {
      await handleFeedbackSubmit(
        data.messageId,
        data.feedback,
        data.agentName,
        data.userMessage,
        data.agentMessage,
        data.feedbackTags,
        data.feedbackNotes,
        data.allowUpdate,
      );
      setShowFeedbackModal(false);
      setPendingFeedback(null);
    },
    [handleFeedbackSubmit],
  );

  // Compute the product set Inquire should send. Priority:
  //   1. Highlighted (checkbox) — the user explicitly curated this set.
  //   2. Faceted/visible — what's left after facet + sort.
  //   3. Full search results.
  // Always capped at INQUIRE_CEILING (15) — base SKUs (color variants are
  // grouped inside each ProductItem just like the chat presenter pipeline).
  const INQUIRE_CEILING = 15;
  const inquireSource = useMemo<'highlight' | 'facet' | 'all'>(() => {
    if (highlightedStyleCodes.length > 0) return 'highlight';
    if (visibleProducts.length > 0 && visibleProducts.length !== products.length) return 'facet';
    return 'all';
  }, [highlightedStyleCodes.length, visibleProducts.length, products.length]);
  const inquireProducts = useMemo<ProductItem[]>(() => {
    const norm = (s?: string | null) => (s ? String(s).toUpperCase().replace(/[^A-Z0-9]/g, '') : '');
    let pool: ProductItem[];
    if (highlightedStyleCodes.length > 0) {
      const hSet = new Set(highlightedStyleCodes.map((c) => norm(c)));
      pool = (visibleProducts.length ? visibleProducts : products).filter((p) =>
        hSet.has(norm(p.base_sku)) || hSet.has(norm(p.partialSku_OPIM)) || hSet.has(norm(p.attributes?.style_code as string | undefined))
      );
    } else if (visibleProducts.length > 0) {
      pool = visibleProducts;
    } else {
      pool = products;
    }
    return pool.slice(0, INQUIRE_CEILING);
  }, [highlightedStyleCodes, visibleProducts, products]);

  const runInquire = useCallback(async (followUpText?: string) => {
    const followUp = (followUpText || '').trim();
    const isFollowUp = followUp.length > 0;
    const query = lastQuery || searchInput.trim();
    if (isInquiring) return;
    // A follow-up reuses the SAME product set the last Inquire ran on; a fresh
    // Inquire needs a query + on-screen products.
    if (!isFollowUp && (!query || !products.length)) return;
    if (isFollowUp && !query && !(lastInquireRef.current?.products?.length)) return;

    const topProducts =
      isFollowUp && lastInquireRef.current?.products?.length
        ? lastInquireRef.current.products
        : inquireProducts;

    // Focus-scoped qualitative context for the Inquire prompt: hard
    // constraints + soft wants from the focused node and its ancestors, plus
    // the labels of every active/scoped filter. Lets the presenter ground its
    // guidance in what the rep is qualifying for.
    const focusedForInquire = inquiryGraph.nodes[focusedNodeId];
    const inqChain: InquiryNode[] = [];
    {
      let c: InquiryNode | undefined = focusedForInquire;
      const seen = new Set<string>();
      while (c && !seen.has(c.id)) {
        seen.add(c.id);
        inqChain.push(c);
        c = c.parentId ? inquiryGraph.nodes[c.parentId] : undefined;
      }
    }
    const inqConstraints = Array.from(
      new Set(inqChain.flatMap((n) => n.constraints || []))
    );
    const inqWants = Array.from(new Set(inqChain.flatMap((n) => n.wants || [])));
    const scopedForInquire = computeScopedFilters(inquiryGraph.nodes, focusedNodeId);
    const inqFilterLabels = Array.from(
      new Set([
        ...activeFilterTags.map((t) => t.label),
        ...scopedForInquire.labels,
      ])
    );

    inquireAbortRef.current?.abort();
    const controller = new AbortController();
    inquireAbortRef.current = controller;

    // Remember the inquired set so follow-up chat turns can keep the same
    // inquiry context of products even if this focus has no bookmarked products.
    lastInquireRef.current = {
      nodeId: focusedNodeId,
      query,
      products: topProducts,
    };

    // Inquiry-graph snapshot so the agent knows where in the inquiry it is.
    const focusRollupForInquire = nodeRollups[focusedNodeId];
    const graphSummary = {
      total_nodes: Object.values(inquiryGraph.nodes).filter(
        (n) => n.kind !== 'root'
      ).length,
      focus_label: focusedForInquire?.label,
      focus_num_items:
        focusRollupForInquire?.numItems ?? topProducts.length,
    };
    // Conversation so far (focus-scoped; includes the prior Inquire breakdown).
    const inquireHistory = isFollowUp ? chatContext.history : undefined;

    const userMsgId = `user-${Date.now()}`;
    const agentMsgId = `agent-${Date.now()}`;
    const inquireScope =
      inquireSource === 'highlight'
        ? 'highlighted'
        : inquireSource === 'facet'
        ? 'filtered'
        : 'top';
    const inquireNow = Date.now();
    setChatMessages((prev) => [
      ...prev,
      {
        id: userMsgId,
        role: 'user',
        content: isFollowUp
          ? followUp
          : `Inquire on ${topProducts.length} ${inquireScope} result${topProducts.length === 1 ? '' : 's'} for “${query}”`,
        timestamp: new Date(inquireNow),
        isCompleted: true,
        nodeId: focusedNodeId,
      },
      {
        id: agentMsgId,
        role: 'agent',
        content: '',
        isStreaming: true,
        timestamp: new Date(inquireNow),
        isCompleted: false,
        replyToId: userMsgId,
        nodeId: focusedNodeId,
        isInquire: true,
      },
    ]);
    scrollChatToBottom();
    setIsInquiring(true);

    let accumulated = '';

    try {
      await streamCollaboratorInquire(
        query,
        topProducts,
        (event) => {
          if (event.type === 'error') {
            accumulated = (event.error as string) || 'Inquire failed';
          } else if (event.type === 'content_final' && typeof event.content === 'string') {
            // Presenter signals final consolidated content — replace accumulator.
            accumulated = event.content;
          } else if (
            (event.type === 'content' || event.type === 'response') &&
            typeof event.content === 'string'
          ) {
            // Presenter streams deltas as type='content'. Older paths use 'response'.
            accumulated += event.content;
          }
          setChatMessages((prev) =>
            prev.map((m) =>
              m.id === agentMsgId ? { ...m, content: accumulated, isStreaming: true } : m
            )
          );
          scrollChatToBottom();
        },
        {
          fastMode,
          signal: controller.signal,
          constraints: inqConstraints,
          wants: inqWants,
          filterLabels: inqFilterLabels,
          followUp: isFollowUp ? followUp : undefined,
          history: inquireHistory,
          graphSummary,
        }
      );
      setChatMessages((prev) =>
        prev.map((m) =>
          m.id === agentMsgId
            ? {
                ...m,
                content: accumulated || 'No response.',
                isStreaming: false,
                isCompleted: true,
              }
            : m
        )
      );
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        const msg = err instanceof Error ? err.message : 'Inquire failed';
        setChatMessages((prev) =>
          prev.map((m) =>
            m.id === agentMsgId
              ? { ...m, content: msg, isStreaming: false, isCompleted: true }
              : m
          )
        );
      }
    } finally {
      setIsInquiring(false);
      scrollChatToBottom();
    }
  }, [lastQuery, searchInput, products, inquireProducts, inquireSource, isInquiring, fastMode, scrollChatToBottom, inquiryGraph.nodes, focusedNodeId, activeFilterTags, nodeRollups, chatContext]);

  // Entry point for the optional Inquire question box. The lightweight
  // chat/filter subagent sees the follow-up first and classifies it:
  //   - ACTION (filter / search / mixed / focus switch) → hand off to the
  //     chat/filter subagent, which applies it (chips, re-search, focus).
  //   - DISCOURSE (discuss / answer / a question) → the presenter responds
  //     with a product-grounded inquiry follow-up on the same set.
  // Empty box → a plain Inquire on the current product set.
  // This lets discussion happen around an inquiry without losing the ability
  // to act on it mid-conversation.
  const submitInquiryQuestion = useCallback(
    async (rawText: string) => {
      const text = (rawText || '').trim();
      if (isInquiring || isFiltering || isSearching) return;
      if (!text) {
        await runInquire();
        return;
      }
      let result: CollaboratorFilterResponse | undefined;
      try {
        result = await collaboratorChatFilter(text, activeBoosts, chatContext);
      } catch {
        result = undefined;
      }
      const isAction =
        !!result &&
        result.success &&
        (result.mode === 'filter' ||
          result.mode === 'search' ||
          result.mode === 'mixed' ||
          !!result.set_focus);
      if (isAction) {
        // Pass it to the lightweight subagent (reuse the prefetched result).
        await runChatFilter(text, result);
      } else {
        // Discourse → the presenter answers with full inquiry context.
        await runInquire(text);
      }
    },
    [
      isInquiring,
      isFiltering,
      isSearching,
      runInquire,
      runChatFilter,
      activeBoosts,
      chatContext,
    ]
  );

  // Style-code tokens + scroll targets — mirrors ChatInterface: only link real
  // SKUs from the visible catalog set, and use the same product-card-* ids as
  // ProductDisplay.buildScrollMeta.
  const catalogProducts = visibleProducts.length > 0 ? visibleProducts : products;

  const { styleCodeToScrollId, styleCodeToCanonicalKey, styleCodeTokens } = useMemo(() => {
    const scrollMap = new Map<string, string>();
    const canonicalMap = new Map<string, string>();
    const tokens: string[] = [];
    const seen = new Set<string>();
    const slugify = (s?: string | null) =>
      s ? String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : '';

    const pushToken = (token?: string, canonical?: string, scrollId?: string) => {
      const n = normStyleToken(token);
      if (!n || n.length < 3) return;
      if (scrollId) scrollMap.set(n, scrollId);
      if (canonical) canonicalMap.set(n, canonical);
      if (!seen.has(n)) {
        seen.add(n);
        tokens.push(n);
      }
    };

    for (const p of catalogProducts) {
      const base = normStyleToken(p.base_sku);
      const partial = normStyleToken(p.partialSku_OPIM);
      const style = normStyleToken(p.attributes?.style_code);
      const canonical = base || style || partial;
      const id = `product-card-${base || style || partial || slugify(p.name) || 'product'}`;
      if (!canonical) continue;
      pushToken(base, canonical, id);
      pushToken(partial, canonical, id);
      pushToken(style, canonical, id);
      if (Array.isArray(p.color_variants)) {
        for (const v of p.color_variants) {
          pushToken(v.sku, canonical, id);
        }
      }
    }
    return {
      styleCodeToScrollId: scrollMap,
      styleCodeToCanonicalKey: canonicalMap,
      styleCodeTokens: tokens,
    };
  }, [catalogProducts]);

  const styleCodeTokensRegex = useMemo(() => {
    if (styleCodeTokens.length === 0) return null;
    const sorted = [...styleCodeTokens].sort((a, b) => b.length - a.length);
    const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(${sorted.map(escape).join('|')})`, 'gi');
  }, [styleCodeTokens]);

  const highlightedTokenSet = useMemo(() => {
    const s = new Set<string>();
    highlightedStyleCodes.forEach((c) => {
      const n = normStyleToken(c);
      if (n) s.add(n);
    });
    return s;
  }, [highlightedStyleCodes]);

  const handleStyleCodeClick = useCallback(
    (code: string) => {
      const normalized = normStyleToken(code);
      const canonical = styleCodeToCanonicalKey.get(normalized) || normalized;
      const scrollId = styleCodeToScrollId.get(normalized);
      if (!scrollId) return;

      // Toggle persistent highlight on the card (checkbox state) like normal chat.
      setHighlightedStyleCodes((prev) => {
        const set = new Set(prev.map(normStyleToken).filter(Boolean));
        if (set.has(canonical)) set.delete(canonical);
        else set.add(canonical);
        return Array.from(set);
      });

      const FLASH_CLASSES = ['ring-2', 'ring-offset-2', 'ring-blue-500', 'shadow-md', 'bg-blue-50/50'];
      const attempt = () => {
        const el = document.getElementById(scrollId);
        if (!el) return false;
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        window.requestAnimationFrame(() => {
          try {
            el.classList.add(...FLASH_CLASSES);
            window.setTimeout(() => {
              try {
                el.classList.remove(...FLASH_CLASSES);
              } catch {}
            }, 1400);
          } catch {}
        });
        return true;
      };

      if (attempt()) return;
      const showMoreButtons = Array.from(
        document.querySelectorAll('[data-product-show-more="true"]')
      ) as HTMLButtonElement[];
      showMoreButtons.forEach((btn) => {
        try {
          btn.click();
        } catch {}
      });
      window.setTimeout(attempt, 220);
    },
    [styleCodeToScrollId, styleCodeToCanonicalKey]
  );

  // Drag handle for resizing the divider between work and chat panels
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const raw = (e.clientX - rect.left) / rect.width;
      const clamped = Math.max(MIN_WORK_RATIO, Math.min(MAX_WORK_RATIO, raw));
      setWorkRatio(clamped);
    };
    const handleUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAB_WORK_RATIO_KEY, String(workRatio));
    } catch {
      /* ignore */
    }
  }, [workRatio]);

  // Render markdown with style-code → button injection. Strip product anchor
  // markers since cards are already visible in the work panel.
  const renderAgentContent = useCallback(
    (content: string, isStreaming?: boolean) => {
      let cleaned = (content || '').replace(PRODUCT_ANCHOR_RE, '').trimStart();
      cleaned = cleaned.replace(HIGHLIGHTED_OPTIONS_HEADING_RE, '').replace(/\n{3,}/g, '\n\n').trim();
      cleaned = stripBoldAroundStyleCodes(cleaned, styleCodeTokens);
      cleaned = stripBackticksAroundStyleCodes(cleaned, styleCodeTokens);

      const linkifyStyleCodes = (text: string, keyPrefix: string): React.ReactNode[] | string => {
        if (!styleCodeTokensRegex) return text;
        const nodes: React.ReactNode[] = [];
        let lastIndex = 0;
        let m: RegExpExecArray | null;
        const regex = new RegExp(styleCodeTokensRegex.source, styleCodeTokensRegex.flags);
        let key = 0;
        while ((m = regex.exec(text)) !== null) {
          if (m.index > lastIndex) nodes.push(text.slice(lastIndex, m.index));
          const matched = m[0];
          const normalized = normStyleToken(matched);
          const canonical = styleCodeToCanonicalKey.get(normalized) || normalized;
          if (!styleCodeToScrollId.has(normalized)) {
            nodes.push(matched);
            lastIndex = m.index + matched.length;
            continue;
          }
          const isActive = highlightedTokenSet.has(canonical);
          nodes.push(
            <button
              key={`${keyPrefix}-${key++}-${m.index}`}
              type="button"
              className={`ps-collab-style-code${isActive ? ' ps-collab-style-code--active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleStyleCodeClick(matched);
              }}
            >
              {matched}
            </button>
          );
          lastIndex = m.index + matched.length;
        }
        if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
        return nodes.length > 0 ? nodes : text;
      };

      const transformChildren = (children: React.ReactNode): React.ReactNode[] =>
        React.Children.toArray(children).flatMap((child, idx) => {
          if (typeof child !== 'string') return [child];
          const linked = linkifyStyleCodes(child, `sc-${idx}`);
          return Array.isArray(linked) ? linked : [linked];
        });

      const components: any = {
        p: ({ children }: any) => (
          <p className="ps-collab-md-p">{transformChildren(children)}</p>
        ),
        li: ({ children }: any) => (
          <li className="ps-collab-md-li">{transformChildren(children)}</li>
        ),
        strong: ({ children }: any) => (
          <strong>{transformChildren(children)}</strong>
        ),
        em: ({ children }: any) => <em>{transformChildren(children)}</em>,
        ul: ({ children }: any) => <ul className="ps-collab-md-ul">{children}</ul>,
        ol: ({ children }: any) => <ol className="ps-collab-md-ol">{children}</ol>,
        h1: ({ children }: any) => <h3 className="ps-collab-md-h">{transformChildren(children)}</h3>,
        h2: ({ children }: any) => <h3 className="ps-collab-md-h">{transformChildren(children)}</h3>,
        h3: ({ children }: any) => <h3 className="ps-collab-md-h">{transformChildren(children)}</h3>,
        // Safety net: any style codes that survived as `<code>` (e.g. from
        // fenced code blocks or unusual markdown) still become clickable.
        code: ({ children }: any) => <code>{transformChildren(children)}</code>,
      };

      return (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {cleaned || (isStreaming ? '…' : '')}
        </ReactMarkdown>
      );
    },
    [
      styleCodeToScrollId,
      styleCodeToCanonicalKey,
      styleCodeTokens,
      styleCodeTokensRegex,
      highlightedTokenSet,
      handleStyleCodeClick,
    ]
  );

  const canInquire = products.length > 0 && !isInquiring && !isSearching;
  const hasActiveInquiry = products.length > 0 || Boolean(lastQuery) || isInquiring;

  // Breadcrumb path: Inquiry root › … › focused node. Walks parent chain
  // from the focused node up to the root, then reverses for display.
  const breadcrumbNodes = useMemo(() => {
    const path: InquiryNode[] = [];
    let curr: InquiryNode | undefined = inquiryGraph.nodes[focusedNodeId];
    while (curr) {
      path.unshift(curr);
      curr = curr.parentId ? inquiryGraph.nodes[curr.parentId] : undefined;
    }
    return path.map((n, idx) => ({
      key: n.id,
      label:
        n.kind === 'root'
          ? 'Explore'
          : n.kind === 'customer'
          ? n.label
          : n.label,
      onClick:
        n.id === focusedNodeId
          ? undefined
          : n.kind === 'root'
          ? clearInquiry
          : () => focusNode(n.id),
      active: idx === path.length - 1,
      kind: n.kind,
    }));
  }, [inquiryGraph.nodes, focusedNodeId, clearInquiry, focusNode]);

  return (
    <div
      className={`ps-collaborator ${isDragging ? 'ps-collaborator--dragging' : ''}`}
      ref={containerRef}
      style={{
        ['--ps-work-flex' as any]: `${workRatio}`,
        ['--ps-chat-flex' as any]: `${1 - workRatio}`,
      }}
    >
      <section className="ps-collaborator__work" aria-label="Catalog work panel">
        <form
          className="ps-collaborator__search-dock ps-collaborator__search-dock--top"
          onSubmit={(e) => {
            e.preventDefault();
            runSearch();
          }}
        >
          <span className="ps-collaborator__dock-label">Search</span>
          <Search className="h-4 w-4 ps-collaborator__dock-icon" aria-hidden />
          <input
            type="search"
            className="ps-collaborator__dock-input ps-collaborator__dock-input--search"
            placeholder='Shorthand search, e.g. "insulated coveralls"'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            disabled={isSearching}
            aria-label="Semantic product search"
          />
          <button
            type="submit"
            className="ps-collaborator__dock-send"
            disabled={isSearching || !searchInput.trim()}
            aria-label="Run search"
            title="Run search"
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </button>
        </form>

        <div className="ps-collaborator__toolbar">
          <div className="ps-collaborator__actions-row">
            <div className="ps-collaborator__inquire-group">
              <input
                type="text"
                className="ps-collaborator__inquire-ask-input"
                placeholder="Optional: ask a question — e.g. “which is best for outdoors?”"
                value={inquireFollowUp}
                onChange={(e) => setInquireFollowUp(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canInquire) {
                    e.preventDefault();
                    const q = inquireFollowUp.trim();
                    setInquireFollowUp('');
                    submitInquiryQuestion(q);
                  }
                }}
                disabled={isInquiring || isSearching || isFiltering}
                aria-label="Optional question for Inquire"
              />
              <button
                type="button"
                className={
                  'ps-collaborator__btn ps-collaborator__btn--secondary' +
                  (inquireSource === 'highlight' ? ' ps-collaborator__btn--highlight' : '')
                }
                onClick={() => {
                  const q = inquireFollowUp.trim();
                  setInquireFollowUp('');
                  submitInquiryQuestion(q);
                }}
                disabled={!canInquire}
                title={
                  inquireFollowUp.trim()
                    ? `Inquire about “${inquireFollowUp.trim()}” using these ${inquireProducts.length} product${inquireProducts.length === 1 ? '' : 's'}`
                    : inquireSource === 'highlight'
                    ? `Inquire on ${inquireProducts.length} highlighted product${inquireProducts.length === 1 ? '' : 's'}`
                    : inquireSource === 'facet'
                    ? `Inquire on ${inquireProducts.length} of ${visibleProducts.length} filtered result${visibleProducts.length === 1 ? '' : 's'} (cap 15)`
                    : `Inquire on top ${inquireProducts.length} of ${products.length} result${products.length === 1 ? '' : 's'} (cap 15)`
                }
              >
                {isInquiring ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                <span>
                  Inquire
                  <span className="ps-collaborator__inquire-count"> ({inquireProducts.length})</span>
                </span>
              </button>
            </div>
            <button
              type="button"
              className={
                'ps-collaborator__btn ps-collaborator__btn--ghost' +
                (highlightedStyleCodes.length > 0 ? ' ps-collaborator__btn--highlight' : '')
              }
              onClick={handleBookmark}
              disabled={highlightedStyleCodes.length === 0}
              title={
                highlightedStyleCodes.length === 0
                  ? 'Highlight one or more products to bookmark them onto a node'
                  : `Bookmark ${highlightedStyleCodes.length} product${highlightedStyleCodes.length === 1 ? '' : 's'} ${bookmarkWillCreate ? 'into a new node' : 'onto'} “${bookmarkTargetLabel}”`
              }
            >
              <BookmarkPlus className="h-4 w-4" />
              <span>
                Bookmark
                {highlightedStyleCodes.length > 0 && (
                  <span className="ps-collaborator__inquire-count">
                    {' '}({highlightedStyleCodes.length})
                  </span>
                )}
              </span>
            </button>
            {hasActiveInquiry && (
              <button
                type="button"
                className="ps-collaborator__btn ps-collaborator__btn--ghost"
                onClick={clearInquiry}
                disabled={isSearching || isInquiring}
                title="Clear results and return chat focus to Explore"
              >
                Clear topic
              </button>
            )}
          </div>
          {activeFilterTags.length > 0 && (
            <div className="ps-collaborator__chip-row" role="list">
              {activeFilterTags.map((tag, idx) => (
                <span
                  key={`${tag.field}-${tag.value ?? ''}-${idx}`}
                  className={
                    'ps-collaborator__chip ' +
                    (tag.field === 'term' ? 'ps-collaborator__chip--term' :
                     tag.field.startsWith('penalty_') ? 'ps-collaborator__chip--penalty' :
                     tag.field.startsWith('only_') ? 'ps-collaborator__chip--hard' :
                     'ps-collaborator__chip--boost')
                  }
                  role="listitem"
                  title={
                    tag.field === 'term' && tag.resolved && tag.resolved.length
                      ? tag.resolved
                          .map((r) => `${r.field}=${JSON.stringify(r.value)}`)
                          .join(', ')
                      : undefined
                  }
                >
                  <span>{tag.label}</span>
                  <button
                    type="button"
                    className="ps-collaborator__chip-x"
                    aria-label={`Remove filter ${tag.label}`}
                    onClick={() => removeFilterTag(tag)}
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="ps-collaborator__chip ps-collaborator__chip--clear"
                onClick={clearAllFilters}
              >
                Clear all
              </button>
            </div>
          )}
          {(categorySuggestions.length > 0 || categorySuggestLoading) && (
            <div className="ps-collaborator__cat-suggest" role="list">
              <span className="ps-collaborator__cat-suggest-label">Narrow:</span>
              {categorySuggestLoading && categorySuggestions.length === 0 && (
                <span
                  className="ps-collaborator__cat-suggest-loading"
                  role="status"
                  aria-live="polite"
                >
                  <span className="ps-collaborator__cat-suggest-spinner" aria-hidden="true" />
                  finding filters…
                </span>
              )}
              {categorySuggestions.map((s, idx) => {
                const active = !!activeCategoryTagFor(s);
                return (
                  <button
                    key={`${s.level}-${idx}`}
                    type="button"
                    role="listitem"
                    className={
                      'ps-collaborator__chip ps-collaborator__chip--suggest' +
                      (active ? ' is-active' : '')
                    }
                    onClick={() => toggleCategorySuggestion(s)}
                    title={
                      active
                        ? `Remove filter: ${s.names.join(', ')}`
                        : `Filter to ${s.names.join(' or ')}`
                    }
                  >
                    <span>{active ? '✓ ' : '+ '}{s.names.join(' / ')}</span>
                  </button>
                );
              })}
            </div>
          )}
          {searchError && (
            <p className="ps-collaborator__error" role="alert">
              {searchError}
            </p>
          )}
        </div>

        {highlightedStyleCodes.length > 0 && (
          <div className="ps-collaborator__highlight-bar">
            <span>
              <strong>{highlightedStyleCodes.length}</strong> product
              {highlightedStyleCodes.length === 1 ? '' : 's'} highlighted — Inquire will use just this set.
            </span>
            <button
              type="button"
              className="ps-collaborator__highlight-clear"
              onClick={() => setHighlightedStyleCodes([])}
            >
              Clear highlights
            </button>
          </div>
        )}

        <div className="ps-collaborator__work-body">
          <div className="ps-collaborator__products">
            <ProductDisplay
              data={displayData}
              ProductCardComponent={ProductCard}
              scale={productScale}
              onVisibleProductsChange={setVisibleProducts}
              highlightedStyleCodes={highlightedStyleCodes}
              onHighlightedStyleCodesChange={setHighlightedStyleCodes}
              showAllProducts
            />
          </div>
        </div>
      </section>

      <div
        className="ps-collaborator__resizer"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat panel"
        onMouseDown={handleMouseDown}
      />

      <aside className="ps-collaborator__chat" aria-label="Collaborator chat">
        <div className="ps-collaborator__focus-zone">
          <div className="ps-collaborator__control-bar">
            <nav
              className="ps-collaborator__breadcrumb"
              aria-label="Inquiry focus path"
            >
              {breadcrumbNodes.map((node, idx) => (
                <React.Fragment key={node.key}>
                  {idx > 0 && (
                    <span className="ps-collaborator__breadcrumb-sep" aria-hidden>
                      ›
                    </span>
                  )}
                  {idx === 0 ? (
                    <button
                      type="button"
                      className={
                        'ps-collaborator__breadcrumb-node ps-collaborator__breadcrumb-node--home' +
                        (node.active ? ' ps-collaborator__breadcrumb-node--active' : '')
                      }
                      onClick={node.onClick}
                      title="Clear topic and return to Explore"
                    >
                      <Home className="h-3.5 w-3.5" />
                      <span>{node.label}</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={
                        'ps-collaborator__breadcrumb-node' +
                        (node.active ? ' ps-collaborator__breadcrumb-node--active' : '')
                      }
                      onClick={node.onClick}
                      disabled={!node.onClick}
                      title={node.onClick ? `Focus on “${node.label}”` : node.label}
                    >
                      {node.label}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </nav>
            {(() => {
              const r = nodeRollups[focusedNode.id];
              if (!r || (r.numProducts === 0 && r.estCost === 0)) return null;
              return (
                <span
                  className="ps-collaborator__control-bar-metrics"
                  title="Estimated cost · products in focused node (incl. children)"
                >
                  {formatMoney(r.estCost)} · {r.numProducts}p
                </span>
              );
            })()}
            <div className="ps-collaborator__control-bar-toggles">
              {customerProspect && (
                <button
                  type="button"
                  className={
                    'ps-collaborator__control-toggle' +
                    (showCustomerInfo ? ' is-active' : '')
                  }
                  onClick={() => setShowCustomerInfo((v) => !v)}
                  aria-expanded={showCustomerInfo}
                  title="Customer account details"
                >
                  <Building2 className="h-3.5 w-3.5" />
                  <span>Customer</span>
                </button>
              )}
              <button
                type="button"
                className={
                  'ps-collaborator__control-toggle' +
                  (focusGraphExpanded ? ' is-active' : '')
                }
                onClick={() => setFocusGraphExpanded((v) => !v)}
                aria-expanded={focusGraphExpanded}
                aria-controls="collab-focus-graph"
                title="Inquiry focus graph"
              >
                <Share2 className="h-3.5 w-3.5" />
                <span>Graph</span>
                <span className="ps-collaborator__control-toggle-count">
                  {graphNodeCount}
                </span>
              </button>
              {focusedNode.kind !== 'sku' && (
                <button
                  type="button"
                  className={
                    'ps-collaborator__control-toggle' +
                    (showNodeReqs ? ' is-active' : '')
                  }
                  onClick={() => setShowNodeReqs((v) => !v)}
                  aria-expanded={showNodeReqs}
                  title="Wants & constraints — kept behind the scenes for Scout"
                >
                  <span>Notes</span>
                  {focusedReqCount > 0 && (
                    <span className="ps-collaborator__control-toggle-count">
                      {focusedReqCount}
                    </span>
                  )}
                </button>
              )}
              <details className="ps-collaborator__actions-more">
                <summary
                  className="ps-collaborator__control-toggle ps-collaborator__actions-more-toggle"
                  title="Node actions"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </summary>
                <div className="ps-collaborator__actions-more-menu">
                  {focusedNode.kind === 'lane' && focusedNode.searchQuery && (
                    <>
                      <button
                        type="button"
                        className="ps-collaborator__bm-action"
                        onClick={() => focusNode(focusedNode.id)}
                        disabled={focusedNode.laneStatus === 'skipped'}
                        title="Run catalog search for this garment lane"
                      >
                        Search lane
                      </button>
                      {focusedNode.laneStatus !== 'skipped' && (
                        <button
                          type="button"
                          className="ps-collaborator__bm-action"
                          onClick={() => skipLane(focusedNode.id)}
                        >
                          Skip lane
                        </button>
                      )}
                    </>
                  )}
                  {focusedNode.kind === 'topic' && focusedNode.query && (
                    <button
                      type="button"
                      className="ps-collaborator__bm-action"
                      onClick={() => focusNode(focusedNode.id)}
                      title="Restore this topic into the work panel"
                    >
                      Open in work panel
                    </button>
                  )}
                  {highlightedStyleCodes.length > 0 && focusedNode.kind !== 'sku' && (
                    <button
                      type="button"
                      className="ps-collaborator__bm-action ps-collaborator__bm-action--accent"
                      onClick={handleBookmark}
                      title={`Bookmark highlighted products as SKU nodes ${bookmarkWillCreate ? 'into a new node' : 'under'} “${bookmarkTargetLabel}”`}
                    >
                      <BookmarkPlus className="h-3.5 w-3.5" />
                      Bookmark {highlightedStyleCodes.length}
                    </button>
                  )}
                  {((focusedNode.products?.length || 0) > 0 ||
                    focusedSkuChildCount > 0) && (
                    <button
                      type="button"
                      className="ps-collaborator__bm-action"
                      onClick={() => toggleTopicExpanded(focusedNode.id)}
                      title={
                        expandedTopicIds.has(focusedNode.id)
                          ? 'Collapse bookmarked products'
                          : 'Show bookmarked products'
                      }
                    >
                      {expandedTopicIds.has(focusedNode.id) ? '▾ Hide' : '▸ Show'}{' '}
                      {(focusedNode.products?.length || 0) + focusedSkuChildCount}{' '}
                      product
                      {(focusedNode.products?.length || 0) + focusedSkuChildCount === 1
                        ? ''
                        : 's'}
                    </button>
                  )}
                  {focusedNode.kind === 'topic' && (
                    <>
                      <button
                        type="button"
                        className="ps-collaborator__bm-action"
                        onClick={() => exportNodeCsv(focusedNode.id)}
                        title="Export topic to CSV"
                      >
                        <Download className="h-3.5 w-3.5" />
                        CSV
                      </button>
                      <button
                        type="button"
                        className="ps-collaborator__bm-action"
                        onClick={() => exportNodePdf(focusedNode.id)}
                        title="Export topic to PDF"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        PDF
                      </button>
                    </>
                  )}
                  {focusedNode.kind !== 'sku' && (
                    <button
                      type="button"
                      className="ps-collaborator__bm-action"
                      onClick={() => {
                        const label = window.prompt(
                          'New child node label (e.g. "Gloves", "Boots"):'
                        );
                        if (label) addChildNode(focusedNode.id, label);
                      }}
                      title="Add a child placeholder node"
                    >
                      + Add child
                    </button>
                  )}
                  {focusedNode.kind !== 'sku' && focusedNode.kind !== 'root' && (
                    <button
                      type="button"
                      className="ps-collaborator__bm-action"
                      onClick={() => {
                        const next = window.prompt('Rename node:', focusedNode.label);
                        if (next) renameNode(focusedNode.id, next);
                      }}
                      title="Rename node"
                    >
                      Rename
                    </button>
                  )}
                  {focusedNode.kind !== 'root' && (
                    <button
                      type="button"
                      className="ps-collaborator__bm-action ps-collaborator__bm-action--danger"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete "${focusedNode.label}"? This removes the node and its children.`
                          )
                        ) {
                          removeNode(focusedNode.id);
                        }
                      }}
                      title="Delete node"
                      aria-label="Delete node"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </button>
                  )}
                </div>
              </details>
            </div>
          </div>
          {showCustomerInfo && customerProspect && (
            <div className="ps-collaborator__customer-info">
              <div className="ps-collaborator__customer-info-line">
                {formatCustomerGraphLine(customerProspect)}
              </div>
              <div className="ps-collaborator__customer-info-date">
                Date of inquiry:{' '}
                <strong>{customerProspect.inquiryDate || '—'}</strong>
              </div>
            </div>
          )}

          {focusGraphExpanded && (
            <div id="collab-focus-graph" className="ps-collaborator__graph ps-collaborator__graph--chat">
              <InquiryFocusGraphD3
                graph={inquiryGraph}
                focusedNodeId={focusedNodeId}
                expandedTopicIds={expandedTopicIds}
                onFocusNode={focusNode}
                onToggleTopicExpanded={toggleTopicExpanded}
                onSkipLane={skipLane}
                onReparentNode={reparentFilterNode}
                metrics={nodeRollups}
              />
            </div>
          )}
          {showNodeReqs && focusedNode.kind !== 'sku' && (
            <div className="ps-collaborator__node-reqs">
              <div className="ps-collaborator__node-reqs-hint">
                Wants &amp; Constraints — kept behind the scenes for Scout; edit
                here if needed.
              </div>
              {/* Constraints (hard requirements) */}
              <div className="ps-collaborator__node-reqs-group">
                      <span className="ps-collaborator__node-reqs-label ps-collaborator__node-reqs-label--constraint">
                        Constraints
                      </span>
                      {(focusedNode.constraints || []).map((c) => (
                        <button
                          key={`c-${c}`}
                          type="button"
                          className="ps-collaborator__req-chip ps-collaborator__req-chip--constraint"
                          onClick={() => removeNodeReq(focusedNode.id, 'constraints', c)}
                          title="Remove constraint"
                        >
                          {c} <span aria-hidden>×</span>
                        </button>
                      ))}
                      {(chatContext.focus_node?.inherited_constraints || []).map((c) => (
                        <span
                          key={`ci-${c}`}
                          className="ps-collaborator__req-chip ps-collaborator__req-chip--inherited"
                          title="Inherited from a parent node"
                        >
                          {c}
                        </span>
                      ))}
                      <button
                        type="button"
                        className="ps-collaborator__req-add"
                        onClick={() => {
                          const v = window.prompt('Add constraint (e.g. "CAT 2", "no polyester"):');
                          if (v) addNodeReq(focusedNode.id, 'constraints', v);
                        }}
                      >
                        +
                      </button>
                      {(focusedNode.constraints || []).length > 0 && (
                        <button
                          type="button"
                          className="ps-collaborator__bm-action"
                          onClick={() =>
                            applySuggestedAction({
                              type: 'filter',
                              label: 'Filter by constraints',
                              terms: focusedNode.constraints,
                            })
                          }
                          title="Ask the filter agent to turn these constraints into catalog filters and search"
                          disabled={isFiltering || isSearching}
                        >
                          Filter by constraints
                        </button>
                      )}
                    </div>
                    {/* Wants (soft preferences) */}
                    <div className="ps-collaborator__node-reqs-group">
                      <span className="ps-collaborator__node-reqs-label ps-collaborator__node-reqs-label--want">
                        Wants
                      </span>
                      {(focusedNode.wants || []).map((w) => (
                        <button
                          key={`w-${w}`}
                          type="button"
                          className="ps-collaborator__req-chip ps-collaborator__req-chip--want"
                          onClick={() => removeNodeReq(focusedNode.id, 'wants', w)}
                          title="Remove want"
                        >
                          {w} <span aria-hidden>×</span>
                        </button>
                      ))}
                      {(chatContext.focus_node?.inherited_wants || []).map((w) => (
                        <span
                          key={`wi-${w}`}
                          className="ps-collaborator__req-chip ps-collaborator__req-chip--inherited"
                          title="Inherited from a parent node"
                        >
                          {w}
                        </span>
                      ))}
                      <button
                        type="button"
                        className="ps-collaborator__req-add"
                        onClick={() => {
                          const v = window.prompt('Add want (e.g. "lightweight garments"):');
                          if (v) addNodeReq(focusedNode.id, 'wants', v);
                        }}
                      >
                        +
                      </button>
                    </div>
            </div>
          )}
        </div>
        <form
          className="ps-collaborator__chat-dock ps-collaborator__chat-dock--top"
          onSubmit={(e) => {
            e.preventDefault();
            runChatFilter();
          }}
        >
          <input
            type="text"
            className="ps-collaborator__dock-input ps-collaborator__dock-input--chat"
            placeholder={
              products.length > 0
                ? 'Discuss this topic, refine filters, or ask Scout to search…'
                : 'Discuss industry, garments, or filters before you search…'
            }
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            disabled={isFiltering || isSearching}
            aria-label="Collaborator chat"
          />
          <button
            type="submit"
            className="ps-collaborator__dock-send ps-collaborator__dock-send--chat"
            disabled={!chatInput.trim() || isFiltering || isSearching}
            aria-label="Send message"
            title="Send message"
          >
            {isFiltering ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MessageSquarePlus className="h-4 w-4" />
            )}
          </button>
        </form>
        <div className="ps-collaborator__chat-messages">
          {chatMessages.length === 0 ? (
            <p className="ps-collaborator__chat-empty">
              Kick off by describing your customer — e.g. <em>“Working with Golden Valley Food Processing, a food processor in the Southwest, contact Jennifer Martinez, national account.”</em> Scout will set the industry filter and garment lanes, then suggest types and filters. When you have results, chat stays scoped to <strong>TOPIC</strong> above. Click a style code in replies to highlight it in the grid.
            </p>
          ) : (
            chatMessages.map((msg) => {
              if (msg.role === 'user') {
                // User bubble — right-aligned, no avatar (matches Aldo).
                return (
                  <div
                    key={msg.id}
                    className="ps-collaborator__bubble ps-collaborator__bubble--user"
                  >
                    <span>{msg.content}</span>
                  </div>
                );
              }
              // Agent message — avatar + content + (when completed) action row.
              const fb = getFeedbackForMessage(msg.id);
              const messageFeedback = fb
                ? {
                    positive: fb.feedback === 'positive',
                    negative: fb.feedback === 'negative',
                  }
                : null;
              const showActionRow = msg.isCompleted && !msg.isStreaming;
              // Strip the [PRODUCTS:…] / [PRODUCT_CARDS:…] anchors and any
              // leading whitespace before we measure "is the bubble empty?".
              // Anchors are stripped at render time anyway and never visible.
              const visibleContent = (msg.content || '')
                .replace(PRODUCT_ANCHOR_RE, '')
                .trimStart();
              const isAwaitingFirstToken = msg.isStreaming && !visibleContent;
              return (
                <div key={msg.id} className="ps-collab-agent-row">
                  {agent && (
                    <div className="ps-collab-agent-row__avatar">
                      <AgentIcon icon={agent.icon} alt={agent.name} size="medium" />
                    </div>
                  )}
                  <div className="ps-collab-agent-row__body">
                    {isAwaitingFirstToken ? (
                      // Pre-token placeholder — a single ellipsis, rendered
                      // inline (no bubble background) so the empty gray
                      // rectangle from before is gone.
                      <div className="ps-collab-agent-row__thinking" aria-live="polite" aria-busy="true">
                        <span className="ps-collab-thinking-dot" />
                        <span className="ps-collab-thinking-dot" />
                        <span className="ps-collab-thinking-dot" />
                      </div>
                    ) : (
                      <div className="ps-collab-agent-row__content">
                        {msg.isStreaming ? (
                          // Token-by-token reveal via our progressive helper.
                          // It uses our own renderer so style codes appear as
                          // clickable buttons from the moment they show up,
                          // not just after the stream completes.
                          <ProgressiveAgentContent
                            content={visibleContent}
                            isStreaming
                            renderer={renderAgentContent}
                          />
                        ) : (
                          // Post-stream: same renderer, full content.
                          renderAgentContent(msg.content, false)
                        )}
                      </div>
                    )}
                    {showActionRow && msg.suggestedActions && msg.suggestedActions.length > 0 && (
                      <div className="ps-collab-suggested-actions" role="group" aria-label="Suggested next steps">
                        {msg.suggestedActions.map((action, i) => (
                          <button
                            key={`${msg.id}-action-${i}`}
                            type="button"
                            className={`ps-collab-suggested-action ps-collab-suggested-action--${action.type}`}
                            onClick={() => applySuggestedAction(action)}
                            disabled={isFiltering || isSearching}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {showActionRow && agent && (
                      <div className="ps-collab-agent-row__actions">
                        <span className="ps-collab-agent-row__time">
                          {msg.timestamp.toLocaleTimeString([], {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </span>
                        <div className="ps-collab-agent-row__feedback">
                          <button
                            type="button"
                            className={`ps-collab-feedback-btn ${
                              messageFeedback?.positive ? 'is-active-up' : ''
                            }`}
                            onClick={() => handleThumbsClick(msg, 'positive')}
                            title="This response was helpful"
                            aria-label="Mark response as helpful"
                          >
                            <ThumbsUp size={18} />
                          </button>
                          <button
                            type="button"
                            className={`ps-collab-feedback-btn ${
                              messageFeedback?.negative ? 'is-active-down' : ''
                            }`}
                            onClick={() => handleThumbsClick(msg, 'negative')}
                            title="This response was not helpful"
                            aria-label="Mark response as not helpful"
                          >
                            <ThumbsDown size={18} />
                          </button>
                          <button
                            type="button"
                            className="ps-collab-feedback-btn"
                            onClick={() => handleCommentClick(msg)}
                            title="Add a comment"
                            aria-label="Add a comment"
                          >
                            <MessageSquare size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={chatEndRef} />
        </div>
      </aside>

      {/* Feedback modal — same component as the main chat. Mounted at the
          Collaborator root so it overlays the whole panel (not just the
          right column). */}
      <FeedbackModal
        isOpen={showFeedbackModal && !!pendingFeedback}
        onClose={() => {
          setShowFeedbackModal(false);
          setPendingFeedback(null);
        }}
        defaultTags={pendingFeedback?.existingTags || []}
        defaultNotes={pendingFeedback?.existingNotes || ''}
        notesOnly={pendingFeedback?.notesOnly}
        showFeedbackSelector={pendingFeedback?.allowFeedbackSelection}
        defaultFeedback={pendingFeedback?.feedback}
        onSubmit={async (tags, notes, feedbackOverride) => {
          if (!pendingFeedback) return;
          await handleModalSubmit({
            messageId: pendingFeedback.messageId,
            feedback: feedbackOverride || pendingFeedback.feedback,
            agentName: pendingFeedback.agentName,
            userMessage: pendingFeedback.userMessage,
            agentMessage: pendingFeedback.agentMessage,
            feedbackTags: tags,
            feedbackNotes: notes,
            allowUpdate: pendingFeedback.allowUpdate,
          });
        }}
      />
    </div>
  );
});

/**
 * Progressive markdown reveal — animates `visibleLength` from its previous
 * value up to `content.length` using `requestAnimationFrame` + easeOutCubic
 * (~15ms/char, clamped 150-1000ms per chunk). Mirrors the animation curve of
 * `components/StreamingText.tsx`, but delegates the actual markdown rendering
 * to the parent's `renderer` callback so we keep the Collaborator-specific
 * style-code → button transform. The result is token-by-token reveal in the
 * chat panel where style codes are clickable from the moment they appear.
 */
interface ProgressiveAgentContentProps {
  content: string;
  isStreaming: boolean;
  renderer: (content: string, isStreaming?: boolean) => React.ReactNode;
}

const ProgressiveAgentContent: React.FC<ProgressiveAgentContentProps> = ({
  content,
  isStreaming,
  renderer,
}) => {
  const [visibleLength, setVisibleLength] = useState(content.length);
  const animationRef = useRef<number | null>(null);
  const prevLengthRef = useRef(content.length);

  useEffect(() => {
    const target = content.length;
    const prevTarget = prevLengthRef.current;

    // Content reset (e.g. content_final replacing accumulator) — snap to 0
    // then animate up. Without this the reveal would appear to jump backward.
    if (target < prevTarget) {
      setVisibleLength(0);
      prevLengthRef.current = target;
      const id = window.setTimeout(() => {
        prevLengthRef.current = target;
        startReveal(0, target);
      }, 20);
      return () => window.clearTimeout(id);
    }

    if (target === prevTarget) return;
    prevLengthRef.current = target;

    function startReveal(start: number, end: number) {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
      const duration = Math.min(1000, Math.max(150, (end - start) * 15));
      const startTime = performance.now();
      const step = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // easeOutCubic for smooth deceleration into the new content
        const eased = 1 - Math.pow(1 - progress, 3);
        const next = Math.floor(start + (end - start) * eased);
        setVisibleLength(next);
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(step);
        } else {
          animationRef.current = null;
        }
      };
      animationRef.current = requestAnimationFrame(step);
    }

    setVisibleLength((current) => {
      startReveal(current, target);
      return current;
    });

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [content]);

  // Once streaming completes, snap to full length so users don't wait on the
  // tail of the animation to interact with style-code buttons.
  useEffect(() => {
    if (!isStreaming) {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      setVisibleLength(content.length);
      prevLengthRef.current = content.length;
    }
  }, [isStreaming, content.length]);

  const visible = content.slice(0, Math.min(visibleLength, content.length));
  return <>{renderer(visible, isStreaming)}</>;
};

export default ProductCollaboratorView;
